import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Wand2, Image, RefreshCw, Code2, ExternalLink, 
  Copy, Check, Download, Loader2, ChevronRight, Lightbulb,
  FileText, Mail, MessageSquare, Megaphone, PenTool, Palette,
  Video, Music, Mic, Globe, Layout, Smartphone, Zap, Film,
  Upload, Play, Volume2, ImagePlus, Type, MousePointer, Users, Hash, X,
  Tv, Radio, Monitor, Link2, FolderOpen, Trash2, Edit, Eye, Calendar, Clock,
  MoreVertical, Search, Filter, ChevronDown, Tablet, Send, Presentation
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { LogoIcon } from '../components/Logo';
import { GridPattern, FloatingParticles, GradientOrb } from '../components/BackgroundEffects';
import { cn } from '../lib/utils';

// External tools data
const EXTERNAL_TOOLS = {
  text: [
    { name: 'ChatGPT', description: 'Conversational AI for any task', url: 'https://chat.openai.com', icon: '🤖' },
    { name: 'Claude', description: 'Advanced reasoning & writing', url: 'https://claude.ai', icon: '🧠' },
    { name: 'Perplexity', description: 'AI-powered research assistant', url: 'https://perplexity.ai', icon: '🔍' },
    { name: 'Jasper', description: 'Marketing copy generator', url: 'https://jasper.ai', icon: '✍️' },
    { name: 'Copy.ai', description: 'AI copywriting tool', url: 'https://copy.ai', icon: '📝' },
    { name: 'Writesonic', description: 'AI content writer', url: 'https://writesonic.com', icon: '⚡' },
  ],
  image: [
    { name: 'Midjourney', description: 'Stunning AI artwork', url: 'https://midjourney.com', icon: '🎨' },
    { name: 'DALL-E 3', description: 'OpenAI image generation', url: 'https://openai.com/dall-e-3', icon: '🖼️' },
    { name: 'Canva AI', description: 'Design with AI features', url: 'https://canva.com', icon: '🎯' },
    { name: 'Remove.bg', description: 'Remove image backgrounds', url: 'https://remove.bg', icon: '✂️' },
    { name: 'Upscale.media', description: 'AI image upscaler', url: 'https://upscale.media', icon: '🔍' },
    { name: 'Leonardo AI', description: 'AI art generation', url: 'https://leonardo.ai', icon: '🦁' },
  ],
  video: [
    { name: 'Kling AI', description: 'Professional AI video generation', url: 'https://klingai.com/global', icon: '🎬' },
    { name: 'Runway', description: 'AI video generation', url: 'https://runway.ml', icon: '🎥' },
    { name: 'HeyGen', description: 'AI avatar videos', url: 'https://heygen.com', icon: '👤' },
    { name: 'Synthesia', description: 'AI video presenter', url: 'https://synthesia.io', icon: '🎭' },
    { name: 'Descript', description: 'Video editing with AI', url: 'https://descript.com', icon: '✂️' },
    { name: 'Luma AI', description: 'Text to 3D video', url: 'https://lumalabs.ai', icon: '🌟' },
    { name: 'Pika Labs', description: 'AI video creation', url: 'https://pika.art', icon: '⚡' },
  ],
  audio: [
    { name: 'ElevenLabs', description: 'AI voice generation', url: 'https://elevenlabs.io', icon: '🎙️' },
    { name: 'Murf AI', description: 'Text to speech', url: 'https://murf.ai', icon: '🔊' },
    { name: 'Suno', description: 'AI music generation', url: 'https://suno.com/invite/@oneforcelive', icon: '🎵' },
    { name: 'Adobe Podcast', description: 'AI audio enhancement', url: 'https://podcast.adobe.com', icon: '🎧' },
    { name: 'Otter.ai', description: 'AI transcription', url: 'https://otter.ai', icon: '📝' },
    { name: 'Speechify', description: 'Text to audio', url: 'https://speechify.com', icon: '📖' },
  ],
  design: [
    { name: 'Emergent.sh', description: 'Build apps, websites & automations with AI agents - white-label partner', url: 'https://app.emergent.sh/?via=ai-horsepowered', icon: '🚀' },
    { name: 'Figma AI', description: 'Design with AI', url: 'https://figma.com', icon: '🎨' },
    { name: 'Framer', description: 'AI website builder', url: 'https://framer.com', icon: '🖥️' },
    { name: 'Looka', description: 'AI logo design', url: 'https://looka.com', icon: '✨' },
    { name: 'Brandmark', description: 'AI brand identity', url: 'https://brandmark.io', icon: '🏷️' },
    { name: 'Uizard', description: 'AI UI design', url: 'https://uizard.io', icon: '📱' },
    { name: 'Gamma', description: 'AI presentations', url: 'https://gamma.app', icon: '📊' },
  ],
  alltools: [
    { name: 'Emergent.sh', description: 'Build apps, websites & automations with AI agents - white-label partner', url: 'https://app.emergent.sh/?via=ai-horsepowered', icon: '🚀' },
    { name: 'Galaxy.ai', description: 'Explore 3000+ AI tools in one place - the ultimate AI directory', url: 'https://galaxy.ai/?ref=ai-horsepowered', icon: '🌌' },
    { name: 'Suno', description: 'Create original music and songs with AI - from any genre to any style', url: 'https://suno.com/invite/@oneforcelive', icon: '🎵' },
  ],
};

const TOOL_CATEGORIES = [
  { id: 'builtin', label: 'Built-in Tools', icon: Sparkles },
  { id: 'myprojects', label: 'My Projects', icon: FolderOpen },
  { id: 'alltools', label: '3000+ AI Tools', icon: Globe },
  { id: 'text', label: 'Text & Copy', icon: FileText },
  { id: 'image', label: 'Images', icon: Image },
  { id: 'video', label: 'Video', icon: Video },
  { id: 'audio', label: 'Audio', icon: Music },
  { id: 'design', label: 'Design', icon: Palette },
];

export default function DIYToolsPage() {
  const [activeCategory, setActiveCategory] = useState('builtin');
  const [activeTool, setActiveTool] = useState(null);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8 pt-20 relative">
        {/* Background */}
        <div className="fixed inset-0 pointer-events-none">
          <GridPattern className="opacity-20" />
          <FloatingParticles count={8} />
          <GradientOrb size="lg" position="top-right" className="opacity-30" />
        </div>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 relative z-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <LogoIcon size="lg" />
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">
                DIY AI Tools Hub
              </h1>
              <p className="text-lg text-primary font-medium">
                Create content, images, and more — powered by AI
              </p>
            </div>
          </div>
          <p className="text-gray-400 mt-4 max-w-3xl">
            Access powerful AI tools to create your own marketing materials, content, and digital assets. 
            Use our built-in tools or explore curated external resources.
          </p>
        </motion.div>

        {/* Category Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-2 mb-8 relative z-10"
        >
          {TOOL_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setActiveTool(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                  activeCategory === cat.id
                    ? 'bg-primary text-black font-medium'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <Icon className="w-4 h-4" />
                {cat.label}
              </button>
            );
          })}
        </motion.div>

        {/* Content Area */}
        <div className="relative z-10">
          <AnimatePresence mode="wait">
            {activeCategory === 'builtin' ? (
              <BuiltInTools 
                key="builtin" 
                activeTool={activeTool} 
                setActiveTool={setActiveTool} 
              />
            ) : activeCategory === 'myprojects' ? (
              <ProjectHistory key="myprojects" />
            ) : (
              <ExternalToolsGrid 
                key={activeCategory} 
                category={activeCategory} 
                tools={EXTERNAL_TOOLS[activeCategory] || []} 
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </DashboardLayout>
  );
}

// Built-in Tools Component
function BuiltInTools({ activeTool, setActiveTool }) {
  const tools = [
    // Cinematic Ad Toolkit - FIRST (above all other tools)
    { id: 'cinematic-headlines', name: 'AI Headlines Generator', description: 'Create attention-grabbing headlines for ads', icon: Type, color: 'cyan', featured: true, category: 'Cinematic Ad Toolkit' },
    { id: 'cinematic-adcopy', name: 'AI Ad Copy Writer', description: 'Compelling ad copy for any platform', icon: PenTool, color: 'cyan', featured: true, category: 'Cinematic Ad Toolkit' },
    { id: 'cinematic-cta', name: 'CTA Generator', description: 'High-converting call-to-action buttons', icon: MousePointer, color: 'cyan', category: 'Cinematic Ad Toolkit' },
    { id: 'cinematic-audience', name: 'Audience Builder', description: 'Define your target audience personas', icon: Users, color: 'cyan', category: 'Cinematic Ad Toolkit' },
    { id: 'cinematic-hooks', name: 'Video Hook Generator', description: 'First 3 seconds that stop the scroll', icon: Film, color: 'cyan', category: 'Cinematic Ad Toolkit' },
    { id: 'cinematic-scripts', name: 'Ad Script Writer', description: 'Full video scripts for cinematic ads', icon: FileText, color: 'cyan', category: 'Cinematic Ad Toolkit' },
    { id: 'cinematic-captions', name: 'Caption Generator', description: 'Engaging captions with hashtags', icon: Hash, color: 'cyan', category: 'Cinematic Ad Toolkit' },
    { id: 'cinematic-landing', name: 'Landing Page Copy', description: 'Convert clicks into customers', icon: Layout, color: 'cyan', category: 'Cinematic Ad Toolkit' },
    
    // Ad Tools Suite - NEW
    { id: 'ad-generator', name: 'AI Ad Generator', description: 'Generate high-converting ads for any platform', icon: Sparkles, color: 'purple', category: 'Ad Tools Suite' },
    
    // Best of Best Creators - NEW
    { id: 'postcard-creator', name: 'Post Card Creator', description: 'Design front & back with correct postage dimensions', icon: Mail, color: 'amber', category: 'Best of Best Creators' },
    { id: 'tv-commercial-creator', name: 'TV Commercial Creator', description: 'Script & storyboard for TV spots', icon: Tv, color: 'amber', category: 'Best of Best Creators' },
    { id: 'radio-ad-creator', name: 'Radio Ad Creator', description: 'Audio scripts for radio broadcast', icon: Radio, color: 'amber', category: 'Best of Best Creators' },
    { id: 'billboard-creator', name: 'Digital Billboard Creator', description: 'High-impact billboard designs', icon: Monitor, color: 'amber', category: 'Best of Best Creators' },
    
    // Featured Tools
    { id: 'elevenlabs', name: 'ElevenLabs Voice AI', description: 'Ultra-realistic text-to-speech voices', icon: Mic, color: 'cyan', featured: true, category: 'Create Content' },
    
    // Cinematic Templates - Featured at top of toolkit
    { id: 'cinematic-templates', name: 'Cinematic Ad Templates', description: 'Ready-to-use video scripts, ad copy & landing pages', icon: Layout, color: 'cyan', featured: true, category: 'Cinematic Ad Toolkit' },
    
    // Content Creation
    { id: 'klingai', name: 'Kling AI Studio', description: 'Professional AI video, image & avatar generation', icon: Video, color: 'pink', category: 'Create Content', new: true },
    { id: 'copywriter', name: 'AI Copywriter', description: 'Generate marketing copy, headlines, emails & more', icon: PenTool, color: 'primary', category: 'Create Content' },
    { id: 'image', name: 'AI Image Generator', description: 'Create stunning visuals with Nano Banana', icon: Image, color: 'secondary', category: 'Create Content' },
    { id: 'rewriter', name: 'Content Rewriter', description: 'Improve, simplify, or transform your text', icon: RefreshCw, color: 'accent', category: 'Create Content' },
    { id: 'adbuilder', name: 'AI Ad Campaign Builder', description: 'Generate Facebook, Google, TikTok & LinkedIn ads', icon: Megaphone, color: 'orange', category: 'Marketing & Growth', new: true },
    { id: 'podcast', name: 'AI Podcast Creator', description: 'Generate scripts, voiceovers & edited audio', icon: Mic, color: 'purple', category: 'Create Content', new: true },
    
    // Lead Generation
    { id: 'leadscraper', name: 'AI Lead Scraper', description: 'Find B2B/B2C leads with emails & contacts', icon: Globe, color: 'blue', category: 'Generate Leads', new: true },
    { id: 'leadqualifier', name: 'Smart Lead Qualifier', description: 'Score leads as Hot/Warm/Cold automatically', icon: Zap, color: 'yellow', category: 'Generate Leads', new: true },
    { id: 'coldemail', name: 'Cold Email & SMS Builder', description: 'Create outreach sequences with AI', icon: Mail, color: 'cyan', category: 'Generate Leads', new: true },
    
    // Sales Enablement
    { id: 'salesscripts', name: 'AI Sales Script Generator', description: 'Phone, text, demo & chat scripts', icon: MessageSquare, color: 'green', category: 'Grow & Retain', new: true },
    { id: 'conversiontester', name: 'Conversion Copy Tester', description: 'A/B test headlines, CTAs & layouts', icon: Code2, color: 'primary', category: 'Grow & Retain', new: true },
    { id: 'analyticsinterpreter', name: 'AI Analytics Interpreter', description: 'Explain traffic dips & conversion bottlenecks', icon: Lightbulb, color: 'amber', category: 'Grow & Retain', new: true },
    
    // Business Admin
    { id: 'legaldocs', name: 'AI Legal Doc Generator', description: 'NDAs, contracts, privacy policies & more', icon: FileText, color: 'blue', category: 'Business Admin', new: true },
    { id: 'contractanalyzer', name: 'AI Contract Analyzer', description: 'Highlight risks & explain legal terms', icon: FileText, color: 'red', category: 'Business Admin', new: true },
    { id: 'businessplanner', name: 'Business Planner & SWOT', description: 'Generate SWOT analysis & action plans', icon: Layout, color: 'green', category: 'Business Admin', new: true },
    { id: 'seoauditor', name: 'AI SEO Optimizer', description: 'Audit sites, keywords & meta tags', icon: Globe, color: 'orange', category: 'Business Admin', new: true },
    
    // Brand & Creative
    { id: 'brandkit', name: 'AI Brand Kit Builder', description: 'Generate style guides & social templates', icon: Palette, color: 'pink', category: 'Brand & Creative', new: true },
    { id: 'appbuilder', name: 'App/Website Builder', description: 'Design your project — we deploy it', icon: Layout, color: 'blue', category: 'Brand & Creative' },
    
    // Productivity
    { id: 'meetingplanner', name: 'AI Meeting Summarizer', description: 'Summarize calls with action items', icon: Video, color: 'blue', category: 'Productivity', new: true },
    { id: 'knowledgebase', name: 'AI Knowledge Base Builder', description: 'Auto-FAQ extraction & searchable docs', icon: FileText, color: 'primary', category: 'Productivity', new: true },
  ];

  // Group tools by category - Ad Tools Suite first for the embedded generator
  const categoryOrder = ['Cinematic Ad Toolkit', 'Best of Best Creators', 'Create Content', 'Marketing & Growth', 'Generate Leads', 'Grow & Retain', 'Business Admin', 'Brand & Creative', 'Productivity'];
  const categories = categoryOrder.filter(cat => tools.some(t => t.category === cat));

  if (activeTool) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
      >
        <button
          onClick={() => setActiveTool(null)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to tools
        </button>
        
        {activeTool === 'elevenlabs' && <ElevenLabsStudio />}
        {activeTool === 'copywriter' && <CopywriterTool />}
        {activeTool === 'image' && <ImageGeneratorTool />}
        {activeTool === 'rewriter' && <ContentRewriterTool />}
        {activeTool === 'appbuilder' && <AppBuilderTool />}
        {activeTool === 'adbuilder' && <AdCampaignBuilder />}
        {activeTool === 'klingai' && <KlingAIStudio />}
        {activeTool === 'leadscraper' && <LeadScraperTool />}
        {activeTool === 'leadqualifier' && <LeadQualifierTool />}
        {activeTool === 'coldemail' && <ColdEmailBuilder />}
        {activeTool === 'salesscripts' && <SalesScriptGenerator />}
        {activeTool === 'legaldocs' && <LegalDocGenerator />}
        {activeTool === 'contractanalyzer' && <ContractAnalyzerTool />}
        {activeTool === 'businessplanner' && <BusinessPlannerTool />}
        {activeTool === 'seoauditor' && <SEOAuditorTool />}
        {activeTool === 'brandkit' && <BrandKitBuilder />}
        {activeTool === 'podcast' && <PodcastCreator />}
        {activeTool === 'conversiontester' && <ConversionTester />}
        {activeTool === 'analyticsinterpreter' && <AnalyticsInterpreter />}
        {activeTool === 'meetingplanner' && <MeetingSummarizer />}
        {activeTool === 'knowledgebase' && <KnowledgeBaseBuilder />}
        
        {/* Cinematic Ad Toolkit Tools */}
        {activeTool === 'cinematic-headlines' && <CinematicHeadlineGenerator />}
        {activeTool === 'cinematic-adcopy' && <CinematicAdCopyWriter />}
        {activeTool === 'cinematic-cta' && <CinematicCTAGenerator />}
        {activeTool === 'cinematic-audience' && <CinematicAudienceBuilder />}
        {activeTool === 'cinematic-hooks' && <CinematicHookGenerator />}
        {activeTool === 'cinematic-scripts' && <CinematicScriptWriter />}
        {activeTool === 'cinematic-captions' && <CinematicCaptionGenerator />}
        {activeTool === 'cinematic-landing' && <CinematicLandingPageCopy />}
        {activeTool === 'cinematic-templates' && <CinematicTemplates />}
        
        {/* Best of Best Creator Tools */}
        {activeTool === 'postcard-creator' && <PostCardCreator />}
        {activeTool === 'tv-commercial-creator' && <TVCommercialCreator />}
        {activeTool === 'radio-ad-creator' && <RadioAdCreator />}
        {activeTool === 'billboard-creator' && <BillboardCreator />}
        
        {/* Ad Tools Suite */}
        {activeTool === 'ad-generator' && <AdGeneratorTool />}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      {/* AI Ad Generator - EMBEDDED AT TOP */}
      <div className="mb-8">
        <AdGeneratorTool embedded={true} />
      </div>

      {/* Featured Tool - ElevenLabs */}
      <div className="max-w-xl">
        {/* ElevenLabs */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setActiveTool('elevenlabs')}
          className="w-full glass rounded-2xl p-5 text-left border border-cyan-500/30 hover:border-cyan-500/50 transition-all group relative overflow-hidden"
          data-testid="tool-elevenlabs"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 opacity-50" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Mic className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors">
                    ElevenLabs Voice AI
                  </h3>
                  <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">Audio</span>
                </div>
                <p className="text-gray-400 text-sm">Ultra-realistic text-to-speech voices</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {['50+ Voices', 'Multilingual', 'Voice Cloning'].map((f) => (
                <span key={f} className="px-2 py-0.5 bg-white/5 text-gray-500 text-xs rounded">{f}</span>
              ))}
            </div>
          </div>
        </motion.button>
      </div>

      {/* Tools by Category */}
      {categories.map((category) => (
        <div key={category} className="space-y-4">
          <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
            {category}
            <span className="text-xs text-gray-500 font-normal">
              ({tools.filter(t => t.category === category && !t.featured).length} tools)
            </span>
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tools.filter(t => t.category === category && !t.featured).map((tool, i) => {
              const Icon = tool.icon;
              const colorMap = {
                primary: 'text-primary bg-primary/20',
                secondary: 'text-secondary bg-secondary/20',
                accent: 'text-accent bg-accent/20',
                green: 'text-green-400 bg-green-500/20',
                blue: 'text-blue-400 bg-blue-500/20',
                purple: 'text-purple-400 bg-purple-500/20',
                pink: 'text-pink-400 bg-pink-500/20',
                orange: 'text-orange-400 bg-orange-500/20',
                yellow: 'text-yellow-400 bg-yellow-500/20',
                red: 'text-red-400 bg-red-500/20',
                amber: 'text-amber-400 bg-amber-500/20',
                cyan: 'text-cyan-400 bg-cyan-500/20',
              };
              const colors = colorMap[tool.color] || colorMap.primary;
              const [textColor, bgColor] = colors.split(' ');
              
              return (
                <motion.button
                  key={tool.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => setActiveTool(tool.id)}
                  className="glass rounded-xl p-4 text-left hover:border-primary/30 transition-all group"
                  data-testid={`tool-${tool.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-5 h-5 ${textColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-heading font-semibold text-white group-hover:text-primary transition-colors truncate">
                          {tool.name}
                        </h4>
                        {tool.new && (
                          <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex-shrink-0">NEW</span>
                        )}
                      </div>
                      <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{tool.description}</p>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      ))}
    </motion.div>
  );
}

// ElevenLabs Studio Tool
function ElevenLabsStudio() {
  const [text, setText] = useState('');
  const [voiceId, setVoiceId] = useState('21m00Tcm4TlvDq8ikWAM'); // Rachel
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [result, setResult] = useState(null);
  
  // Voice settings
  const [stability, setStability] = useState(0.5);
  const [similarityBoost, setSimilarityBoost] = useState(0.75);
  const [style, setStyle] = useState(0.0);
  
  // Load voices on mount
  useEffect(() => {
    const loadVoices = async () => {
      try {
        const response = await api.elevenLabsVoices();
        setVoices(response.data.voices || []);
      } catch (error) {
        console.error('Failed to load voices:', error);
        toast.error('Failed to load voices');
      } finally {
        setLoadingVoices(false);
      }
    };
    loadVoices();
  }, []);

  const handleGenerate = async () => {
    if (!text.trim()) {
      toast.error('Please enter text to convert');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.elevenLabsTTS({
        text,
        voice_id: voiceId,
        stability,
        similarity_boost: similarityBoost,
        style
      });
      setResult(response.data);
      toast.success('Audio generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate audio');
    } finally {
      setLoading(false);
    }
  };

  const selectedVoice = voices.find(v => v.voice_id === voiceId);

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
          <Mic className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">ElevenLabs Voice AI</h2>
          <p className="text-sm text-gray-400">Ultra-realistic text-to-speech voices</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Voice Selection */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Select Voice</label>
          {loadingVoices ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading voices...
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-1">
              {voices.map((voice) => (
                <button
                  key={voice.voice_id}
                  onClick={() => setVoiceId(voice.voice_id)}
                  className={`p-3 rounded-xl text-left transition-all ${
                    voiceId === voice.voice_id
                      ? 'bg-cyan-500/20 border border-cyan-500/50'
                      : 'bg-white/5 border border-transparent hover:bg-white/10'
                  }`}
                >
                  <p className={`text-sm font-medium truncate ${voiceId === voice.voice_id ? 'text-cyan-400' : 'text-white'}`}>
                    {voice.name}
                  </p>
                  {voice.category && (
                    <p className="text-xs text-gray-500 mt-0.5">{voice.category}</p>
                  )}
                </button>
              ))}
            </div>
          )}
          {selectedVoice?.preview_url && (
            <div className="mt-2">
              <audio src={selectedVoice.preview_url} controls className="w-full h-8" />
              <p className="text-xs text-gray-500 mt-1">Preview voice</p>
            </div>
          )}
        </div>

        {/* Text Input */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Text to Convert *</label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter the text you want to convert to speech..."
            className="input-dark min-h-[120px]"
            data-testid="elevenlabs-text"
          />
          <p className="text-xs text-gray-500 mt-1">{text.length} characters</p>
        </div>

        {/* Voice Settings */}
        <div className="space-y-4 p-4 rounded-xl bg-white/5">
          <h4 className="text-sm font-medium text-white">Voice Settings</h4>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Stability</span>
              <span className="text-cyan-400">{stability.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={stability}
              onChange={(e) => setStability(parseFloat(e.target.value))}
              className="w-full accent-cyan-500"
            />
            <p className="text-xs text-gray-500">Lower = more expressive, Higher = more consistent</p>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Similarity Boost</span>
              <span className="text-cyan-400">{similarityBoost.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={similarityBoost}
              onChange={(e) => setSimilarityBoost(parseFloat(e.target.value))}
              className="w-full accent-cyan-500"
            />
            <p className="text-xs text-gray-500">How closely the AI should match the original voice</p>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Style Exaggeration</span>
              <span className="text-cyan-400">{style.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={style}
              onChange={(e) => setStyle(parseFloat(e.target.value))}
              className="w-full accent-cyan-500"
            />
            <p className="text-xs text-gray-500">Amplify the style of the original speaker</p>
          </div>
        </div>

        <Button 
          onClick={handleGenerate} 
          disabled={loading || !text.trim()} 
          className="w-full bg-cyan-500 hover:bg-cyan-600 text-black"
          data-testid="elevenlabs-generate"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Volume2 className="w-4 h-4 mr-2" />}
          Generate Speech
        </Button>

        {/* Result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 rounded-xl bg-white/5 border border-cyan-500/20"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">Generated Audio</span>
              <span className="text-xs text-cyan-400">{result.text_length} characters</span>
            </div>
            <audio src={result.audio_url} controls className="w-full mb-3" />
            <a 
              href={result.audio_url} 
              download={`elevenlabs_${result.audio_id}.mp3`}
              className="flex items-center justify-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm"
            >
              <Download className="w-4 h-4" />
              Download MP3
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// AI Copywriter Tool
function CopywriterTool() {
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('professional');
  const [contentType, setContentType] = useState('general');
  const [wordCount, setWordCount] = useState(200);
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const tones = ['professional', 'casual', 'persuasive', 'friendly', 'formal'];
  const contentTypes = [
    { id: 'general', label: 'General Copy', icon: FileText },
    { id: 'headline', label: 'Headlines', icon: Megaphone },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'social', label: 'Social Media', icon: MessageSquare },
    { id: 'blog', label: 'Blog Post', icon: FileText },
    { id: 'ad', label: 'Ad Copy', icon: Zap },
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    setLoading(true);
    try {
      const response = await api.generateCopy({ prompt, tone, content_type: contentType, word_count: wordCount });
      setResult(response.data.content);
      toast.success('Content generated!');
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
          <PenTool className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">AI Copywriter</h2>
          <p className="text-sm text-gray-400">Generate compelling marketing copy</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Content Type Selection */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Content Type</label>
          <div className="flex flex-wrap gap-2">
            {contentTypes.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => setContentType(type.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    contentType === type.id
                      ? 'bg-primary text-black font-medium'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {type.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tone Selection */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Tone</label>
          <div className="flex flex-wrap gap-2">
            {tones.map((t) => (
              <button
                key={t}
                onClick={() => setTone(t)}
                className={`px-3 py-1.5 rounded-full text-sm capitalize transition-all ${
                  tone === t
                    ? 'bg-secondary text-black font-medium'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Word Count */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Target Word Count: {wordCount}</label>
          <input
            type="range"
            min="50"
            max="500"
            step="50"
            value={wordCount}
            onChange={(e) => setWordCount(Number(e.target.value))}
            className="w-full accent-primary"
          />
        </div>

        {/* Prompt */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">What do you want to write about?</label>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., Write a compelling headline for a real estate agency that specializes in luxury homes..."
            className="input-dark min-h-[120px]"
            data-testid="copywriter-prompt"
          />
        </div>

        <Button 
          onClick={handleGenerate} 
          disabled={loading} 
          className="btn-primary w-full"
          data-testid="copywriter-generate"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Generate Copy
        </Button>

        {/* Result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">Generated Content</span>
              <button onClick={handleCopy} className="flex items-center gap-1 text-primary text-sm hover:underline">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="text-white whitespace-pre-wrap">{result}</div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// AI Image Generator Tool
function ImageGeneratorTool() {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('realistic');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const styles = [
    { id: 'realistic', label: 'Realistic', icon: '📷' },
    { id: 'artistic', label: 'Artistic', icon: '🎨' },
    { id: 'minimalist', label: 'Minimalist', icon: '⬜' },
    { id: 'cinematic', label: 'Cinematic', icon: '🎬' },
    { id: 'illustration', label: 'Illustration', icon: '✏️' },
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.generateImage({ prompt, style });
      setResult(response.data);
      toast.success('Image generated!');
    } catch (error) {
      toast.error('Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
          <Image className="w-6 h-6 text-secondary" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">AI Image Generator</h2>
          <p className="text-sm text-gray-400">Create stunning visuals with Nano Banana</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Style Selection */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Style</label>
          <div className="flex flex-wrap gap-2">
            {styles.map((s) => (
              <button
                key={s.id}
                onClick={() => setStyle(s.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                  style === s.id
                    ? 'bg-secondary text-black font-medium'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                <span>{s.icon}</span>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Prompt */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Describe your image</label>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., A modern office building with glass windows reflecting a sunset, aerial view..."
            className="input-dark min-h-[120px]"
            data-testid="image-prompt"
          />
        </div>

        <Button 
          onClick={handleGenerate} 
          disabled={loading} 
          className="btn-secondary w-full"
          data-testid="image-generate"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
          {loading ? 'Generating...' : 'Generate Image'}
        </Button>

        {/* Result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            <div className="rounded-xl overflow-hidden border border-white/10">
              <img src={result.image_url} alt="Generated" className="w-full" />
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-500 truncate max-w-[70%]">{result.prompt_used}</span>
              <a 
                href={result.image_url} 
                download 
                className="flex items-center gap-1 text-primary text-sm hover:underline"
              >
                <Download className="w-4 h-4" />
                Download
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// Content Rewriter Tool
function ContentRewriterTool() {
  const [originalText, setOriginalText] = useState('');
  const [rewriteStyle, setRewriteStyle] = useState('improve');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const styles = [
    { id: 'improve', label: 'Improve Quality', description: 'Enhance clarity and engagement' },
    { id: 'simplify', label: 'Simplify', description: 'Use simpler words and shorter sentences' },
    { id: 'expand', label: 'Expand', description: 'Add more detail and examples' },
    { id: 'summarize', label: 'Summarize', description: 'Create a concise summary' },
    { id: 'formal', label: 'Make Formal', description: 'Professional business tone' },
    { id: 'casual', label: 'Make Casual', description: 'Friendly conversational tone' },
  ];

  const handleRewrite = async () => {
    if (!originalText.trim()) {
      toast.error('Please enter text to rewrite');
      return;
    }
    setLoading(true);
    try {
      const response = await api.rewriteContent({ original_text: originalText, rewrite_style: rewriteStyle });
      setResult(response.data);
      toast.success('Content rewritten!');
    } catch (error) {
      toast.error('Failed to rewrite content');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result.rewritten_text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 text-accent" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">Content Rewriter</h2>
          <p className="text-sm text-gray-400">Transform your text with AI</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Style Selection */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Rewrite Style</label>
          <div className="grid sm:grid-cols-2 gap-2">
            {styles.map((s) => (
              <button
                key={s.id}
                onClick={() => setRewriteStyle(s.id)}
                className={`p-3 rounded-lg text-left transition-all ${
                  rewriteStyle === s.id
                    ? 'bg-accent/20 border border-accent/50'
                    : 'bg-white/5 border border-transparent hover:bg-white/10'
                }`}
              >
                <p className={`text-sm font-medium ${rewriteStyle === s.id ? 'text-accent' : 'text-white'}`}>
                  {s.label}
                </p>
                <p className="text-xs text-gray-500">{s.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Original Text */}
        <div>
          <label className="block text-sm text-gray-400 mb-2">Original Text</label>
          <Textarea
            value={originalText}
            onChange={(e) => setOriginalText(e.target.value)}
            placeholder="Paste or type the text you want to rewrite..."
            className="input-dark min-h-[150px]"
            data-testid="rewriter-original"
          />
        </div>

        <Button 
          onClick={handleRewrite} 
          disabled={loading} 
          className="bg-accent hover:bg-accent/80 text-black w-full"
          data-testid="rewriter-generate"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Rewrite Content
        </Button>

        {/* Result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">Original: {result.original_length} chars</span>
                <span className="text-xs text-gray-500">→</span>
                <span className="text-xs text-accent">New: {result.new_length} chars</span>
              </div>
              <button onClick={handleCopy} className="flex items-center gap-1 text-accent text-sm hover:underline">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="text-white whitespace-pre-wrap">{result.rewritten_text}</div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// Kling AI Studio - Comprehensive AI Generation Tool
function KlingAIStudio() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass rounded-2xl p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mx-auto mb-4">
          <Video className="w-8 h-8 text-white" />
        </div>
        <h2 className="font-heading text-2xl font-semibold text-white mb-2">Kling AI Studio</h2>
        <p className="text-gray-400 mb-6">Professional AI video, image & avatar generation</p>
        
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full mb-6">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span className="text-yellow-400 font-medium">Coming Soon</span>
        </div>
        
        <p className="text-gray-400 text-sm max-w-md mx-auto mb-8">
          We&apos;re integrating powerful AI generation capabilities including text-to-video, 
          image-to-video, AI avatars, lip sync, virtual try-on, and more. Stay tuned!
        </p>
        
        {/* Feature Preview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: '🎬', name: 'Text to Video' },
            { icon: '🖼️', name: 'Image to Video' },
            { icon: '🎨', name: 'Text to Image' },
            { icon: '👤', name: 'AI Avatar' },
            { icon: '💋', name: 'Lip Sync' },
            { icon: '👕', name: 'Virtual Try-On' },
            { icon: '➕', name: 'Video Extension' },
            { icon: '🔊', name: 'Text to Speech' },
          ].map((feature) => (
            <div key={feature.name} className="p-3 rounded-xl bg-white/5 border border-white/10">
              <span className="text-2xl mb-2 block">{feature.icon}</span>
              <span className="text-xs text-gray-400">{feature.name}</span>
            </div>
          ))}
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="https://klingai.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Try Kling AI Directly
          </a>
          <Button className="btn-primary">
            Contact DFY Team <ChevronRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Need Help CTA */}
      <div className="glass rounded-2xl p-6 text-center">
        <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-3">
          <Users className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">Need Professional Help Now?</h3>
        <p className="text-gray-400 text-sm mb-4">
          Our team can create professional videos, avatars, and content for you using Kling AI and other tools.
        </p>
        <Button className="btn-primary">
          Place a DFY Order <ChevronRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function AppBuilderTool() {
  const [formData, setFormData] = useState({
    project_type: 'landing_page',
    industry: '',
    business_name: '',
    description: '',
    features: [],
    style_preference: 'modern',
    color_preference: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const projectTypes = [
    { id: 'landing_page', label: 'Landing Page', icon: Layout, credits: '500' },
    { id: 'website', label: 'Full Website', icon: Globe, credits: '2,000' },
    { id: 'funnel', label: 'Sales Funnel', icon: Zap, credits: '1,500' },
    { id: 'app', label: 'Web App', icon: Smartphone, credits: '5,000' },
  ];

  const featureOptions = [
    'Contact Form', 'Live Chat', 'Blog Section', 'Gallery', 'Testimonials',
    'FAQ Section', 'Newsletter Signup', 'Social Media Integration', 
    'Video Section', 'Pricing Table', 'Team Section', 'Calendar/Booking'
  ];

  const styleOptions = ['modern', 'classic', 'minimal', 'bold'];

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.business_name || !formData.industry || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const response = await api.createAppProject(formData);
      setResult(response.data);
      toast.success('Project created!');
    } catch (error) {
      toast.error('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!result?.project_id) {
      toast.error('No project to submit');
      return;
    }
    setSubmitting(true);
    try {
      // Submit the project for review - creates an order
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diy/submit-project/${result.project_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          project_type: formData.project_type,
          business_name: formData.business_name,
          estimated_credits: result.estimated_credits
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit');
      }
      
      const data = await response.json();
      setSubmitted(true);
      toast.success('Project submitted for review! Our team will contact you shortly.');
    } catch (error) {
      toast.error('Failed to submit project. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
          <Code2 className="w-6 h-6 text-green-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">App/Website Builder</h2>
          <p className="text-sm text-gray-400">Design your project — our team deploys it</p>
        </div>
      </div>

      {result ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">Project Created!</span>
            </div>
            <p className="text-sm text-gray-300">Project ID: {result.project_id}</p>
          </div>

          <div className="p-4 rounded-xl bg-white/5">
            <h3 className="text-white font-medium mb-3">Project Preview</h3>
            <div className="text-gray-300 text-sm whitespace-pre-wrap prose-invert" 
                 dangerouslySetInnerHTML={{ __html: result.preview_content.replace(/\n/g, '<br/>').replace(/##\s/g, '<strong>').replace(/\*\*/g, '') }} />
          </div>

          <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-medium">Estimated Credits</span>
              <span className="text-primary font-bold text-xl">{result.estimated_credits.toLocaleString()}</span>
            </div>
            <p className="text-xs text-gray-400">Credits will be deducted from your Co-Op balance upon project completion.</p>
          </div>

          <div>
            <h3 className="text-white font-medium mb-3">Next Steps</h3>
            <ol className="space-y-2">
              {result.next_steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </div>

          <div className="flex gap-3">
            <Button onClick={() => { setResult(null); setSubmitted(false); }} variant="outline" className="flex-1">
              Create Another
            </Button>
            {submitted ? (
              <Button disabled className="flex-1 bg-green-600">
                <Check className="w-4 h-4 mr-2" /> Submitted!
              </Button>
            ) : (
              <Button 
                onClick={handleSubmitForReview} 
                disabled={submitting}
                className="btn-primary flex-1"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</>
                ) : (
                  'Submit for Review'
                )}
              </Button>
            )}
          </div>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {/* Project Type */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Project Type *</label>
            <div className="grid sm:grid-cols-2 gap-3">
              {projectTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setFormData(prev => ({ ...prev, project_type: type.id }))}
                    className={`p-4 rounded-xl text-left transition-all ${
                      formData.project_type === type.id
                        ? 'bg-green-500/20 border border-green-500/50'
                        : 'bg-white/5 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-6 h-6 ${formData.project_type === type.id ? 'text-green-400' : 'text-gray-400'}`} />
                      <div>
                        <p className={`font-medium ${formData.project_type === type.id ? 'text-green-400' : 'text-white'}`}>
                          {type.label}
                        </p>
                        <p className="text-xs text-gray-500">~{type.credits} credits</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Business Details */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Business Name *</label>
              <Input
                value={formData.business_name}
                onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                placeholder="Acme Corp"
                className="input-dark"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Industry *</label>
              <Input
                value={formData.industry}
                onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                placeholder="Real Estate, Healthcare, etc."
                className="input-dark"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Project Description *</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what you want to build, your goals, target audience..."
              className="input-dark min-h-[100px]"
            />
          </div>

          {/* Features */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Features (select all that apply)</label>
            <div className="flex flex-wrap gap-2">
              {featureOptions.map((feature) => (
                <button
                  key={feature}
                  onClick={() => handleFeatureToggle(feature)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    formData.features.includes(feature)
                      ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  {feature}
                </button>
              ))}
            </div>
          </div>

          {/* Style & Color */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Style Preference</label>
              <div className="flex flex-wrap gap-2">
                {styleOptions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setFormData(prev => ({ ...prev, style_preference: s }))}
                    className={`px-3 py-1.5 rounded-full text-sm capitalize transition-all ${
                      formData.style_preference === s
                        ? 'bg-primary text-black font-medium'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Color Preference (optional)</label>
              <Input
                value={formData.color_preference}
                onChange={(e) => setFormData(prev => ({ ...prev, color_preference: e.target.value }))}
                placeholder="Blue, Gold, or leave blank"
                className="input-dark"
              />
            </div>
          </div>

          <Button 
            onClick={handleSubmit} 
            disabled={loading} 
            className="bg-green-500 hover:bg-green-600 text-black w-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lightbulb className="w-4 h-4 mr-2" />}
            Generate Project Preview
          </Button>
        </div>
      )}
    </div>
  );
}

// Ad Campaign Builder Tool
function AdCampaignBuilder() {
  const [businessInfo, setBusinessInfo] = useState('');
  const [platform, setPlatform] = useState('facebook');
  const [goal, setGoal] = useState('leads');
  const [budget, setBudget] = useState('50');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!businessInfo.trim()) {
      toast.error('Please describe your business');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Generate a complete ${platform} ad campaign for: ${businessInfo}. Goal: ${goal}. Daily budget: $${budget}. Include: 1) Primary ad copy (headline, description, CTA), 2) 3 ad variations for A/B testing, 3) Target audience suggestions, 4) Placement recommendations, 5) Budget pacing strategy.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Ad Campaign Builder" description="Generate complete ad campaigns for Facebook, Google, TikTok & LinkedIn" icon={Megaphone} color="orange">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Describe Your Business & Offer</label>
          <Textarea
            value={businessInfo}
            onChange={(e) => setBusinessInfo(e.target.value)}
            placeholder="e.g., Local roofing company targeting homeowners aged 30-60 within 30 miles. Goal: generate leads for free roof inspections..."
            className="min-h-[100px]"
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Platform</label>
            <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white">
              <option value="facebook">Facebook/Instagram</option>
              <option value="google">Google Ads</option>
              <option value="tiktok">TikTok</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Goal</label>
            <select value={goal} onChange={(e) => setGoal(e.target.value)} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white">
              <option value="leads">Lead Generation</option>
              <option value="traffic">Website Traffic</option>
              <option value="sales">Sales/Conversions</option>
              <option value="awareness">Brand Awareness</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Daily Budget ($)</label>
            <Input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="50" />
          </div>
        </div>
        <Button onClick={handleGenerate} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating Campaign...</> : 'Generate Ad Campaign'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Lead Scraper Tool
function LeadScraperTool() {
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [companySize, setCompanySize] = useState('any');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleScrape = async () => {
    if (!industry.trim()) {
      toast.error('Please enter an industry');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Generate a list of 10 sample B2B leads for: Industry: ${industry}, Location: ${location || 'Any'}, Company Size: ${companySize}. For each lead include: Company Name, Industry, Location, Estimated Size, Website Domain, Key Contact Title. Note: This is sample data for demonstration. For real lead scraping, place a DFY order.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate leads');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Lead Scraper" description="Find B2B/B2C leads with contact info" icon={Globe} color="blue">
      <div className="space-y-4">
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-300">
          💡 This tool generates sample lead data for demonstration. For real lead scraping with verified emails & contacts, place a DFY order.
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Industry/Vertical</label>
            <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="e.g., Real Estate, SaaS" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Location</label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g., California, USA" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Company Size</label>
            <select value={companySize} onChange={(e) => setCompanySize(e.target.value)} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white">
              <option value="any">Any Size</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="200+">200+ employees</option>
            </select>
          </div>
        </div>
        <Button onClick={handleScrape} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Finding Leads...</> : 'Generate Sample Leads'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Lead Qualifier Tool
function LeadQualifierTool() {
  const [leadInfo, setLeadInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleQualify = async () => {
    if (!leadInfo.trim()) {
      toast.error('Please enter lead information');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Analyze and score this lead: ${leadInfo}. Provide: 1) Lead Score (Hot/Warm/Cold), 2) Likelihood to Convert (percentage), 3) Key Buying Signals, 4) Recommended Next Action, 5) Suggested Outreach Message, 6) Qualification Tags for CRM.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to qualify lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="Smart Lead Qualifier" description="Score leads as Hot/Warm/Cold automatically" icon={Zap} color="yellow">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Lead Information</label>
          <Textarea
            value={leadInfo}
            onChange={(e) => setLeadInfo(e.target.value)}
            placeholder="Paste lead info: name, company, industry, how they found you, their inquiry, budget indicators, timeline..."
            className="min-h-[120px]"
          />
        </div>
        <Button onClick={handleQualify} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing Lead...</> : 'Qualify Lead'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Cold Email Builder
function ColdEmailBuilder() {
  const [context, setContext] = useState('');
  const [emailType, setEmailType] = useState('cold');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!context.trim()) {
      toast.error('Please describe your outreach goal');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Generate a ${emailType} outreach sequence for: ${context}. Create: 1) Subject line options (3 variations), 2) Email 1 (initial outreach), 3) Email 2 (follow-up day 3), 4) Email 3 (final follow-up day 7), 5) SMS message alternative. Make it personalized, non-salesy, and include clear CTAs.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate sequence');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="Cold Email & SMS Builder" description="Create outreach sequences with AI" icon={Mail} color="cyan">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Email Type</label>
          <select value={emailType} onChange={(e) => setEmailType(e.target.value)} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white">
            <option value="cold">Cold Outreach</option>
            <option value="warm">Warm Follow-up</option>
            <option value="reengagement">Re-engagement</option>
            <option value="partnership">Partnership Proposal</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Context & Goal</label>
          <Textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g., Reaching out to marketing agencies to offer white-label web design services. Target: agencies with 5-20 employees who don't have in-house designers..."
            className="min-h-[100px]"
          />
        </div>
        <Button onClick={handleGenerate} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating Sequence...</> : 'Generate Outreach Sequence'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Sales Script Generator
function SalesScriptGenerator() {
  const [product, setProduct] = useState('');
  const [scriptType, setScriptType] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!product.trim()) {
      toast.error('Please describe your product/service');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Generate a ${scriptType} sales script for: ${product}. Include: 1) Opening/Hook, 2) Discovery questions, 3) Value proposition points, 4) Common objection responses (price, timing, competition), 5) Close/CTA, 6) Follow-up steps.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate script');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Sales Script Generator" description="Phone, text, demo & chat scripts" icon={MessageSquare} color="green">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Script Type</label>
          <select value={scriptType} onChange={(e) => setScriptType(e.target.value)} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white">
            <option value="phone">Phone Call</option>
            <option value="text">Text/SMS</option>
            <option value="demo">Demo/Presentation</option>
            <option value="chat">Live Chat</option>
            <option value="video">Video Sales Letter</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Product/Service Description</label>
          <Textarea
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            placeholder="Describe what you're selling, target audience, price point, and key benefits..."
            className="min-h-[100px]"
          />
        </div>
        <Button onClick={handleGenerate} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Writing Script...</> : 'Generate Sales Script'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Legal Document Generator
function LegalDocGenerator() {
  const [docType, setDocType] = useState('nda');
  const [businessInfo, setBusinessInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const docTypes = {
    nda: 'Non-Disclosure Agreement (NDA)',
    service: 'Service Agreement / SOW',
    contractor: 'Independent Contractor Agreement',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    refund: 'Refund / Return Policy'
  };

  const handleGenerate = async () => {
    if (!businessInfo.trim()) {
      toast.error('Please enter business information');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Generate a ${docTypes[docType]} for: ${businessInfo}. Create a professional, legally-sound document template. Include all standard sections. Add placeholders for: [COMPANY NAME], [DATE], [CLIENT NAME], etc. Note: This is a template - recommend legal review before use.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Legal Document Generator" description="NDAs, contracts, privacy policies & more" icon={FileText} color="blue">
      <div className="space-y-4">
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-300">
          ⚠️ These are templates for reference. Always have legal documents reviewed by a qualified attorney before use.
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Document Type</label>
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white">
            {Object.entries(docTypes).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Business Information</label>
          <Textarea
            value={businessInfo}
            onChange={(e) => setBusinessInfo(e.target.value)}
            placeholder="Business name, industry, location, specific terms to include (e.g., payment terms, confidentiality period, jurisdiction)..."
            className="min-h-[100px]"
          />
        </div>
        <Button onClick={handleGenerate} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating Document...</> : 'Generate Legal Document'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Contract Analyzer Tool
function ContractAnalyzerTool() {
  const [contractText, setContractText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAnalyze = async () => {
    if (!contractText.trim()) {
      toast.error('Please paste contract text');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Analyze this contract and provide: 1) Summary of key terms, 2) Risk areas highlighted (RED FLAGS), 3) Important deadlines/dates, 4) Financial obligations, 5) Termination conditions, 6) Recommendations for negotiation points. Contract text: ${contractText.substring(0, 3000)}...`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to analyze contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Contract Analyzer" description="Highlight risks & explain legal terms" icon={FileText} color="red">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Paste Contract Text</label>
          <Textarea
            value={contractText}
            onChange={(e) => setContractText(e.target.value)}
            placeholder="Paste the contract text you want to analyze..."
            className="min-h-[200px]"
          />
        </div>
        <Button onClick={handleAnalyze} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing Contract...</> : 'Analyze Contract'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Business Planner Tool
function BusinessPlannerTool() {
  const [businessIdea, setBusinessIdea] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAnalyze = async () => {
    if (!businessIdea.trim()) {
      toast.error('Please describe your business idea');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Create a business analysis for: ${businessIdea}. Include: 1) SWOT Analysis (Strengths, Weaknesses, Opportunities, Threats), 2) Competitive Landscape (3 main competitors), 3) Target Market Profile, 4) Revenue Model Options, 5) 90-Day Action Plan with specific milestones, 6) Key Success Metrics to track.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate analysis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="Business Planner & SWOT" description="Generate SWOT analysis & action plans" icon={Layout} color="green">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Describe Your Business/Idea</label>
          <Textarea
            value={businessIdea}
            onChange={(e) => setBusinessIdea(e.target.value)}
            placeholder="Describe your business idea, current stage, target market, and goals..."
            className="min-h-[120px]"
          />
        </div>
        <Button onClick={handleAnalyze} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing...</> : 'Generate Business Analysis'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// SEO Auditor Tool
function SEOAuditorTool() {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAudit = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Please enter a website URL');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Generate an SEO audit checklist for: ${websiteUrl}. Target keywords: ${keywords || 'general'}. Include: 1) On-Page SEO checklist (title tags, meta descriptions, headers), 2) Technical SEO items (page speed, mobile, schema), 3) Content recommendations, 4) Keyword opportunities, 5) Backlink strategy suggestions, 6) Priority action items (ranked by impact).`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate audit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI SEO Optimizer" description="Audit sites, keywords & meta tags" icon={Globe} color="orange">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Website URL</label>
          <Input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://yourwebsite.com" />
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Target Keywords (optional)</label>
          <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="e.g., roofing contractor, roof repair" />
        </div>
        <Button onClick={handleAudit} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running Audit...</> : 'Generate SEO Audit'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Brand Kit Builder
function BrandKitBuilder() {
  const [brandInfo, setBrandInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!brandInfo.trim()) {
      toast.error('Please describe your brand');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Create a brand kit for: ${brandInfo}. Include: 1) Brand Voice Guidelines (tone, personality, do's and don'ts), 2) Color Palette (primary, secondary, accent - with hex codes), 3) Typography recommendations, 4) Logo usage guidelines, 5) Social media post templates (formats for FB, IG, LinkedIn), 6) Email signature template, 7) Brand tagline options (3 variations).`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate brand kit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Brand Kit Builder" description="Generate style guides & social templates" icon={Palette} color="pink">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Brand Information</label>
          <Textarea
            value={brandInfo}
            onChange={(e) => setBrandInfo(e.target.value)}
            placeholder="Business name, industry, target audience, brand values, existing colors/fonts (if any), competitors you admire..."
            className="min-h-[120px]"
          />
        </div>
        <Button onClick={handleGenerate} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating Brand Kit...</> : 'Generate Brand Kit'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Podcast Creator
function PodcastCreator() {
  const [topic, setTopic] = useState('');
  const [format, setFormat] = useState('solo');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Create a ${format} podcast episode plan for topic: ${topic}. Include: 1) Episode title options (3), 2) Hook/Intro (30 seconds), 3) Main content outline with timestamps, 4) Key talking points, 5) Listener engagement questions, 6) Call-to-action/outro, 7) Show notes summary, 8) Social media clips to extract.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate podcast plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Podcast Creator" description="Generate scripts, plans & show notes" icon={Mic} color="purple">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Format</label>
          <select value={format} onChange={(e) => setFormat(e.target.value)} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white">
            <option value="solo">Solo Episode</option>
            <option value="interview">Interview Format</option>
            <option value="cohost">Co-Hosted Discussion</option>
            <option value="q&a">Q&A / Listener Questions</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Episode Topic</label>
          <Textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="What's the topic? Who's your audience? Any specific angle or takeaway?"
            className="min-h-[100px]"
          />
        </div>
        <Button onClick={handleGenerate} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating Episode Plan...</> : 'Generate Podcast Plan'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Conversion Tester
function ConversionTester() {
  const [currentCopy, setCurrentCopy] = useState('');
  const [element, setElement] = useState('headline');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!currentCopy.trim()) {
      toast.error('Please enter your current copy');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Generate A/B test variations for this ${element}: "${currentCopy}". Provide: 1) Analysis of current version, 2) 5 alternative variations with different angles (urgency, benefit-focused, curiosity, social proof, fear of missing out), 3) Hypothesis for each variation, 4) Recommended test duration, 5) Success metrics to track.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate variations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="Conversion Copy Tester" description="A/B test headlines, CTAs & layouts" icon={Code2} color="primary">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Element Type</label>
          <select value={element} onChange={(e) => setElement(e.target.value)} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white">
            <option value="headline">Headline</option>
            <option value="cta">Call-to-Action Button</option>
            <option value="subheadline">Subheadline</option>
            <option value="subject">Email Subject Line</option>
            <option value="value-prop">Value Proposition</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Current Copy</label>
          <Textarea
            value={currentCopy}
            onChange={(e) => setCurrentCopy(e.target.value)}
            placeholder="Enter your current headline, CTA, or copy that you want to test..."
            className="min-h-[80px]"
          />
        </div>
        <Button onClick={handleGenerate} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating Variations...</> : 'Generate Test Variations'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Analytics Interpreter
function AnalyticsInterpreter() {
  const [analyticsData, setAnalyticsData] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleInterpret = async () => {
    if (!analyticsData.trim()) {
      toast.error('Please enter your analytics data');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Interpret this analytics data and provide insights: ${analyticsData}. Include: 1) Key takeaways (what's working, what's not), 2) Why traffic/conversions might have changed, 3) Best performing channels/pages, 4) Conversion bottlenecks identified, 5) Specific recommendations to improve metrics, 6) Week-over-week trend analysis.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to interpret data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Analytics Interpreter" description="Explain traffic dips & conversion bottlenecks" icon={Lightbulb} color="amber">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Paste Your Analytics Data</label>
          <Textarea
            value={analyticsData}
            onChange={(e) => setAnalyticsData(e.target.value)}
            placeholder="Paste data from Google Analytics, CRM, or ad platform. Include: traffic numbers, conversion rates, top pages, bounce rates, traffic sources, time periods..."
            className="min-h-[150px]"
          />
        </div>
        <Button onClick={handleInterpret} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing Data...</> : 'Interpret Analytics'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Meeting Summarizer
function MeetingSummarizer() {
  const [meetingNotes, setMeetingNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSummarize = async () => {
    if (!meetingNotes.trim()) {
      toast.error('Please enter meeting notes or transcript');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Summarize this meeting and extract action items: ${meetingNotes}. Provide: 1) Executive Summary (2-3 sentences), 2) Key Discussion Points, 3) Decisions Made, 4) Action Items (with owner and deadline), 5) Open Questions/Follow-ups, 6) Next Meeting Agenda Items.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to summarize meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Meeting Summarizer" description="Summarize calls with action items" icon={Video} color="blue">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Meeting Notes or Transcript</label>
          <Textarea
            value={meetingNotes}
            onChange={(e) => setMeetingNotes(e.target.value)}
            placeholder="Paste your meeting notes, call transcript, or key discussion points..."
            className="min-h-[200px]"
          />
        </div>
        <Button onClick={handleSummarize} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Summarizing...</> : 'Summarize Meeting'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Knowledge Base Builder
function KnowledgeBaseBuilder() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleBuild = async () => {
    if (!content.trim()) {
      toast.error('Please enter your content');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: `Create a knowledge base structure from this content: ${content}. Generate: 1) FAQ extraction (10-15 common questions with answers), 2) Category organization, 3) Key terms glossary, 4) Quick reference guide, 5) Troubleshooting section, 6) Search tags for each section.`
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to build knowledge base');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolWrapper title="AI Knowledge Base Builder" description="Auto-FAQ extraction & searchable docs" icon={FileText} color="primary">
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Source Content</label>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Paste documentation, product info, processes, or any content you want to convert into a searchable knowledge base..."
            className="min-h-[200px]"
          />
        </div>
        <Button onClick={handleBuild} disabled={loading} className="btn-primary w-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Building KB...</> : 'Build Knowledge Base'}
        </Button>
        {result && <ResultDisplay result={result} />}
      </div>
    </ToolWrapper>
  );
}

// Tool Wrapper Component
function ToolWrapper({ title, description, icon: Icon, color, children }) {
  const colorMap = {
    primary: 'text-primary bg-primary/20 border-primary/30',
    secondary: 'text-secondary bg-secondary/20 border-secondary/30',
    accent: 'text-accent bg-accent/20 border-accent/30',
    green: 'text-green-400 bg-green-500/20 border-green-500/30',
    blue: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
    purple: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
    pink: 'text-pink-400 bg-pink-500/20 border-pink-500/30',
    orange: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
    yellow: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
    red: 'text-red-400 bg-red-500/20 border-red-500/30',
    amber: 'text-amber-400 bg-amber-500/20 border-amber-500/30',
    cyan: 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30',
  };
  const colors = colorMap[color] || colorMap.primary;
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-14 h-14 rounded-xl ${colors.split(' ')[1]} flex items-center justify-center`}>
          <Icon className={`w-7 h-7 ${colors.split(' ')[0]}`} />
        </div>
        <div>
          <h2 className="font-heading text-2xl font-bold text-white">{title}</h2>
          <p className="text-gray-400">{description}</p>
        </div>
      </div>
      <div className={`glass rounded-2xl p-6 border ${colors.split(' ')[2]}`}>
        {children}
      </div>
    </motion.div>
  );
}

// Result Display Component  
function ResultDisplay({ result }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-400">Generated Result</span>
        <Button variant="ghost" size="sm" onClick={handleCopy}>
          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <div className="bg-black/30 rounded-xl p-4 max-h-[400px] overflow-y-auto">
        <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-gray-200">
          {result}
        </div>
      </div>
    </motion.div>
  );
}

// External Tools Grid
function ExternalToolsGrid({ category, tools }) {
  const [embedModal, setEmbedModal] = useState(null);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-semibold text-white capitalize">
          {category} Tools
        </h2>
        <span className="text-sm text-gray-400">{tools.length} tools</span>
      </div>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools.map((tool, i) => (
          <motion.a
            key={tool.name}
            href={tool.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="glass rounded-xl p-4 hover:border-primary/30 transition-all group"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{tool.icon}</span>
                <div>
                  <h3 className="font-medium text-white group-hover:text-primary transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-sm text-gray-400">{tool.description}</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
            </div>
          </motion.a>
        ))}
      </div>

      {/* Kling AI Embedded Studio - Only for video category */}
      {category === 'video' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 rounded-2xl overflow-hidden border border-white/10"
        >
          <div className="bg-gradient-to-r from-pink-600/20 via-purple-600/20 to-blue-600/20 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-white">Kling AI Studio</h3>
                <p className="text-sm text-gray-400">Create professional AI videos directly in your Workspace</p>
              </div>
            </div>
            <a
              href="https://klingai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-primary flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Open Full Site
            </a>
          </div>
          <div className="bg-black relative">
            <iframe
              src="https://app.klingai.com/global/image-to-video/frame-mode/new"
              className="w-full h-[700px] border-0"
              title="Kling AI Studio"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone; fullscreen"
              allowFullScreen
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
            />
            {/* Overlay message in case iframe doesn't load properly */}
            <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg p-3 text-center">
              <p className="text-sm text-gray-400">
                If the studio doesn&apos;t load, you may need to{' '}
                <a 
                  href="https://app.klingai.com/global/image-to-video/frame-mode/new" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  open Kling AI in a new tab
                </a>
                {' '}or log in first.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Manus AI Featured Section - Only for alltools category */}
      {category === 'alltools' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 relative overflow-hidden rounded-2xl"
        >
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-cyan-600/20" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDF6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
          
          <div className="relative p-6 md:p-8">
            {/* Header with Meta + Manus logos */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 rounded-full">
                <span className="text-lg">🤖</span>
                <span className="text-sm font-semibold text-blue-400">MANUS AI</span>
              </div>
              <span className="text-gray-500">×</span>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 rounded-full">
                <span className="text-lg">Ⓜ️</span>
                <span className="text-sm font-semibold text-blue-400">META</span>
              </div>
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                500 FREE CREDITS
              </span>
            </div>

            {/* Main Headline */}
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
              The Future of AI Agents is Here
            </h3>
            
            {/* Description */}
            <div className="space-y-3 mb-6 text-gray-300">
              <p>
                <strong className="text-white">Manus has joined Meta</strong> — validating their pioneering work in General AI Agents. 
                This isn&apos;t just another AI tool; it&apos;s a fully autonomous agent that bridges the gap between <em>thinking</em> and <em>doing</em>.
              </p>
              <p>
                With over <strong className="text-cyan-400">147 Trillion tokens processed</strong> and <strong className="text-cyan-400">80 Million virtual computers</strong> created, 
                Manus handles research, automation, and complex tasks end-to-end — so you can focus on what matters.
              </p>
              <p className="text-sm text-gray-400">
                🐎 <strong className="text-primary">AI Horse-Powered recommends:</strong> Perfect for automating repetitive business tasks, 
                deep research projects, and building workflows that run on autopilot. Combine with our DFY services for maximum horsepower.
              </p>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-2 mb-6">
              {['Free Trial', '500 Free Credits', 'Browser Automation', 'Research Agent', 'Code Generation', 'Slack Integration'].map((feature) => (
                <span key={feature} className="px-3 py-1 bg-white/10 text-gray-300 text-sm rounded-full">
                  {feature}
                </span>
              ))}
            </div>

            {/* CTA Button */}
            <a
              href="https://manus.im/invitation/V4VDB3T0AWALK"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-4 px-6 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 hover:from-blue-600 hover:via-purple-600 hover:to-cyan-600 text-white font-semibold text-lg rounded-xl text-center transition-all transform hover:scale-[1.02] shadow-lg shadow-purple-500/25"
            >
              🚀 Try Manus AI Free — Get 500 Credits
            </a>
            
            <p className="text-center text-xs text-gray-500 mt-3">
              Referral link • No credit card required to start
            </p>
          </div>
        </motion.div>
      )}

      <div className="mt-8 p-6 glass rounded-2xl text-center">
        <Lightbulb className="w-8 h-8 text-primary mx-auto mb-3" />
        <h3 className="font-heading text-lg font-semibold text-white mb-2">
          Need help with these tools?
        </h3>
        <p className="text-gray-400 text-sm mb-4">
          Our team can create professional content for you using any of these tools.
        </p>
        <Button className="btn-primary">
          Place a DFY Order <ChevronRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
}


// ==========================================
// CINEMATIC AD TOOLKIT COMPONENTS
// ==========================================

// Generic Cinematic AI Tool Component
function CinematicAITool({ title, icon: Icon, description, placeholder, promptTemplate }) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!input.trim()) {
      toast.error('Please provide input');
      return;
    }
    setLoading(true);
    try {
      const response = await api.sendChatMessage({
        message: promptTemplate.replace('{INPUT}', input)
      });
      setResult(response.data.response);
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <Icon className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-semibold text-white">{title}</h2>
            <p className="text-sm text-gray-400">{description}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Your Input</label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              rows={4}
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:border-cyan-500/50 focus:outline-none resize-none"
            />
          </div>

          <Button
            onClick={handleGenerate}
            className="btn-primary w-full"
            disabled={loading || !input.trim()}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <>Generate <Sparkles className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>
      </div>

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-white">Generated Content</h3>
            <Button variant="ghost" size="sm" onClick={copyToClipboard}>
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <div className="prose prose-invert max-w-none">
            <div 
              className="text-gray-300 whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: result.replace(/\n/g, '<br/>') }}
            />
          </div>
        </motion.div>
      )}
    </div>
  );
}

// Cinematic Headline Generator
function CinematicHeadlineGenerator() {
  return (
    <CinematicAITool
      title="AI Headlines Generator"
      icon={Type}
      description="Create attention-grabbing headlines for your cinematic ads"
      placeholder="Describe your product/service and target audience. E.g., 'Luxury home renovation services for busy professionals in Miami'"
      promptTemplate="Generate 10 compelling ad headlines for: {INPUT}. Include headlines for: 1) Facebook/Instagram, 2) Google Ads, 3) TikTok, 4) YouTube. Make them attention-grabbing, benefit-focused, and include power words. Format each with the platform name and character count."
    />
  );
}

// Cinematic Ad Copy Writer
function CinematicAdCopyWriter() {
  return (
    <CinematicAITool
      title="AI Ad Copy Writer"
      icon={PenTool}
      description="Compelling ad copy for any platform"
      placeholder="Describe your business, offer, and target audience. E.g., 'Real estate agent offering free home valuations to homeowners looking to sell in the next 6 months'"
      promptTemplate="Write compelling ad copy for: {INPUT}. Create: 1) Short-form copy (125 chars for FB/IG), 2) Medium-form copy (250 chars for Google), 3) Long-form copy (500+ chars for landing pages), 4) Story-based copy for video ads. Include emotional triggers, social proof elements, and urgency where appropriate."
    />
  );
}

// CTA Generator
function CinematicCTAGenerator() {
  return (
    <CinematicAITool
      title="CTA Generator"
      icon={MousePointer}
      description="High-converting call-to-action buttons and phrases"
      placeholder="Describe what action you want users to take. E.g., 'Book a free consultation for kitchen remodeling'"
      promptTemplate="Generate 15 high-converting CTAs for: {INPUT}. Include: 1) Button text variations, 2) Urgency-based CTAs, 3) Benefit-focused CTAs, 4) Question-based CTAs, 5) Social proof CTAs. Rate each CTA by expected conversion potential (High/Medium/Low) and explain why."
    />
  );
}

// Audience Builder
function CinematicAudienceBuilder() {
  return (
    <CinematicAITool
      title="Audience Builder"
      icon={Users}
      description="Define your target audience personas"
      placeholder="Describe your business and ideal customer. E.g., 'HVAC company serving residential customers in Phoenix, AZ'"
      promptTemplate="Create detailed audience personas for: {INPUT}. For each persona include: 1) Demographics (age, income, location), 2) Psychographics (interests, values, pain points), 3) Behavioral traits, 4) Facebook/Instagram targeting suggestions, 5) Google keyword themes, 6) TikTok interest categories. Create 3 distinct personas: Primary, Secondary, and Lookalike."
    />
  );
}

// Video Hook Generator
function CinematicHookGenerator() {
  return (
    <CinematicAITool
      title="Video Hook Generator"
      icon={Film}
      description="First 3 seconds that stop the scroll"
      placeholder="Describe your video ad topic and target audience. E.g., 'Weight loss program for women over 40'"
      promptTemplate="Generate 10 attention-grabbing video hooks (first 3 seconds) for: {INPUT}. Include: 1) Question hooks, 2) Shocking stat hooks, 3) Story hooks, 4) Controversy hooks, 5) FOMO hooks. For each hook, provide: the spoken text, visual suggestion, and expected engagement rate (High/Medium/Low)."
    />
  );
}

// Ad Script Writer
function CinematicScriptWriter() {
  return (
    <CinematicAITool
      title="Ad Script Writer"
      icon={FileText}
      description="Full video scripts for cinematic ads"
      placeholder="Describe your product/service, key benefits, and desired video length. E.g., '30-second ad for mortgage refinancing service highlighting low rates'"
      promptTemplate="Write a complete video ad script for: {INPUT}. Include: 1) Opening hook (3 sec), 2) Problem statement (5 sec), 3) Solution introduction (7 sec), 4) Key benefits (10 sec), 5) Social proof (3 sec), 6) CTA (2 sec). Provide both spoken dialogue and visual/scene directions. Also include B-roll suggestions."
    />
  );
}

// Caption Generator
function CinematicCaptionGenerator() {
  return (
    <CinematicAITool
      title="Caption Generator"
      icon={Hash}
      description="Engaging captions with hashtags"
      placeholder="Describe the content of your post/ad. E.g., 'Before and after photos of a bathroom renovation in a luxury home'"
      promptTemplate="Generate engaging social media captions for: {INPUT}. Create: 1) Short caption (1-2 lines), 2) Medium caption (3-4 lines with CTA), 3) Long-form caption (story-style with CTA), 4) 15 relevant hashtags (mix of popular and niche), 5) 3 emoji combinations that enhance the message. Optimize for Instagram and Facebook algorithms."
    />
  );
}

// Landing Page Copy
function CinematicLandingPageCopy() {
  return (
    <CinematicAITool
      title="Landing Page Copy"
      icon={Layout}
      description="Convert clicks into customers"
      placeholder="Describe your offer, target audience, and desired action. E.g., 'Free consultation offer for personal injury law firm targeting car accident victims'"
      promptTemplate="Write conversion-optimized landing page copy for: {INPUT}. Include: 1) Headline + subheadline, 2) Hero section copy, 3) Problem-agitation section, 4) Solution/benefits section (3 key benefits with descriptions), 5) Social proof section (testimonial templates), 6) FAQ section (5 common questions), 7) Final CTA section. Use persuasion principles: urgency, scarcity, social proof, authority."
    />
  );
}

// ==========================================
// CINEMATIC VIDEO AD TEMPLATES
// ==========================================

const CINEMATIC_TEMPLATES = [
  {
    id: 'problem-solution',
    name: 'Problem-Solution Ad',
    duration: '30 seconds',
    description: 'Hook viewers with a relatable problem, then present your solution',
    structure: [
      { time: '0-3s', section: 'Hook', content: '[ATTENTION GRABBER - Question or shocking statement]' },
      { time: '3-8s', section: 'Problem', content: '[Paint the pain point your audience faces]' },
      { time: '8-18s', section: 'Solution', content: '[Introduce your product/service as THE solution]' },
      { time: '18-25s', section: 'Proof', content: '[Show results, testimonials, or demonstrations]' },
      { time: '25-30s', section: 'CTA', content: '[Clear call-to-action with urgency]' },
    ],
    headlines: [
      'Tired of [PROBLEM]? Here\'s the fix.',
      'Stop doing [WRONG APPROACH]. Try this instead.',
      'What if [DESIRED OUTCOME] was this easy?',
    ],
    ctaExamples: ['Book Your Free Call', 'Get Started Today', 'Claim Your Discount'],
  },
  {
    id: 'testimonial-story',
    name: 'Customer Story Ad',
    duration: '45 seconds',
    description: 'Let your happy customers tell their transformation story',
    structure: [
      { time: '0-5s', section: 'Before Hook', content: '[Customer shares their struggle/problem]' },
      { time: '5-15s', section: 'Discovery', content: '[How they found your solution]' },
      { time: '15-30s', section: 'Transformation', content: '[The results they achieved]' },
      { time: '30-40s', section: 'Endorsement', content: '[Why they recommend you]' },
      { time: '40-45s', section: 'CTA', content: '[Invitation to experience the same]' },
    ],
    headlines: [
      '"I never thought I\'d [ACHIEVE RESULT]..."',
      'From [BEFORE STATE] to [AFTER STATE] in [TIMEFRAME]',
      'This changed everything for me.',
    ],
    ctaExamples: ['See More Success Stories', 'Start Your Transformation', 'Get Your Free Quote'],
  },
  {
    id: 'how-to-demo',
    name: 'How-To Demo Ad',
    duration: '60 seconds',
    description: 'Show viewers exactly how your product/service works',
    structure: [
      { time: '0-5s', section: 'Promise', content: '[State what they\'ll learn/achieve]' },
      { time: '5-15s', section: 'Step 1', content: '[First simple step with visual]' },
      { time: '15-30s', section: 'Step 2', content: '[Second step showing ease]' },
      { time: '30-45s', section: 'Step 3', content: '[Final step with result]' },
      { time: '45-55s', section: 'Benefits', content: '[Highlight key advantages]' },
      { time: '55-60s', section: 'CTA', content: '[Get them to take action]' },
    ],
    headlines: [
      'Here\'s how to [ACHIEVE GOAL] in 3 simple steps',
      'Watch me [DO THING] in under a minute',
      'The easiest way to [GET RESULT]',
    ],
    ctaExamples: ['Try It Free', 'Watch Full Tutorial', 'Get Started Now'],
  },
  {
    id: 'local-business',
    name: 'Local Business Ad',
    duration: '30 seconds',
    description: 'Perfect for service businesses targeting local customers',
    structure: [
      { time: '0-3s', section: 'Location Hook', content: '[Call out your city/area]' },
      { time: '3-10s', section: 'Service Intro', content: '[What you do and who you help]' },
      { time: '10-20s', section: 'Trust Builders', content: '[Years in business, reviews, certifications]' },
      { time: '20-27s', section: 'Special Offer', content: '[Limited time deal or free consultation]' },
      { time: '27-30s', section: 'Contact CTA', content: '[Phone number or booking link]' },
    ],
    headlines: [
      '[CITY]\'s #1 Rated [SERVICE]',
      'Trusted by [NUMBER]+ [CITY] families',
      '[SERVICE] experts serving [AREA] since [YEAR]',
    ],
    ctaExamples: ['Call Now for Free Estimate', 'Book Online Today', 'Limited Spots Available'],
  },
  {
    id: 'before-after',
    name: 'Before & After Ad',
    duration: '15 seconds',
    description: 'Quick visual transformation showcase',
    structure: [
      { time: '0-2s', section: 'Before Label', content: '[Show "Before" state with label]' },
      { time: '2-5s', section: 'Before Visual', content: '[The problem/starting point]' },
      { time: '5-7s', section: 'After Label', content: '[Show "After" state with label]' },
      { time: '7-12s', section: 'After Visual', content: '[The amazing result]' },
      { time: '12-15s', section: 'CTA', content: '[Get them to want the same]' },
    ],
    headlines: [
      'The transformation speaks for itself',
      'From [THIS] to [THAT] with [YOUR SERVICE]',
      'Results that make you say WOW',
    ],
    ctaExamples: ['See More Transformations', 'Get Your Quote', 'Book Now'],
  },
];

const AD_COPY_TEMPLATES = [
  {
    id: 'psa',
    name: 'Problem-Agitate-Solution (PAS)',
    description: 'Classic copywriting formula that works every time',
    template: `🎯 PROBLEM: [State the problem your audience faces]

😤 AGITATE: [Make them feel the pain of not solving it]
• [Pain point 1]
• [Pain point 2]  
• [Pain point 3]

✨ SOLUTION: [Present your offer as the answer]

[BENEFIT 1]
[BENEFIT 2]
[BENEFIT 3]

🔥 [SPECIAL OFFER/URGENCY]

👇 [CTA]`,
  },
  {
    id: 'aida',
    name: 'AIDA Formula',
    description: 'Attention → Interest → Desire → Action',
    template: `🚨 [ATTENTION-GRABBING HEADLINE]

Did you know [INTERESTING FACT/STATISTIC]?

Here's what [YOUR SERVICE] can do for you:
✅ [Benefit that creates INTEREST]
✅ [Benefit that creates DESIRE]
✅ [Benefit that creates more DESIRE]

[SOCIAL PROOF - testimonial or stat]

Ready to [ACHIEVE GOAL]?

[CTA BUTTON TEXT] 👇`,
  },
  {
    id: 'story',
    name: 'Story-Based Copy',
    description: 'Connect emotionally through narrative',
    template: `[NAME] was just like you.

[Describe their struggle in 1-2 sentences]

Then they discovered [YOUR SOLUTION].

Within [TIMEFRAME], they:
• [Result 1]
• [Result 2]
• [Result 3]

"[SHORT TESTIMONIAL QUOTE]" - [Name, Title]

Your story can be next.

[CTA]`,
  },
  {
    id: 'list',
    name: 'Listicle Copy',
    description: 'Easy-to-scan benefits list format',
    template: `[NUMBER] reasons why [CITY/AUDIENCE] chooses [YOUR BUSINESS]:

1️⃣ [Reason + brief explanation]
2️⃣ [Reason + brief explanation]
3️⃣ [Reason + brief explanation]
4️⃣ [Reason + brief explanation]
5️⃣ [Reason + brief explanation]

⭐ [RATING] stars from [NUMBER]+ reviews

[SPECIAL OFFER]

[CTA]`,
  },
];

const LANDING_PAGE_TEMPLATES = [
  {
    id: 'lead-gen',
    name: 'Lead Generation Page',
    description: 'Capture leads with a compelling offer',
    sections: ['Hero', 'Problem', 'Solution', 'Features', 'Social Proof', 'FAQ', 'CTA'],
    heroTemplate: `[MAIN HEADLINE - Promise or benefit]
[SUBHEADLINE - How you deliver on promise]

[HERO IMAGE/VIDEO]

[FORM: Name, Email, Phone]
[BUTTON: Get My Free [OFFER]]

✓ [Trust badge 1]  ✓ [Trust badge 2]  ✓ [Trust badge 3]`,
  },
  {
    id: 'booking',
    name: 'Appointment Booking Page',
    description: 'Drive consultations and appointments',
    sections: ['Hero', 'Services', 'Process', 'Testimonials', 'Calendar', 'FAQ'],
    heroTemplate: `Book Your Free [SERVICE] Consultation
[SUBHEADLINE - What they'll get from the call]

[CALENDAR EMBED]

What to expect:
✓ [Benefit 1]
✓ [Benefit 2]  
✓ [Benefit 3]

[TESTIMONIAL SNIPPET]`,
  },
  {
    id: 'offer',
    name: 'Special Offer Page',
    description: 'Promote limited-time deals and discounts',
    sections: ['Countdown', 'Offer', 'Benefits', 'Bonuses', 'Guarantee', 'CTA'],
    heroTemplate: `🔥 [OFFER NAME] - [DISCOUNT/VALUE]

[COUNTDOWN TIMER]

For a limited time, get:
• [Main offer]
• [Bonus 1] (Value: $XX)
• [Bonus 2] (Value: $XX)

Total Value: $[HIGH NUMBER]
Your Price: $[LOW NUMBER]

[GUARANTEE BADGE]

[BUY NOW BUTTON]`,
  },
];

// Cinematic Templates Component
function CinematicTemplates() {
  const [activeCategory, setActiveCategory] = useState('video');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [copied, setCopied] = useState(false);

  const copyTemplate = (content) => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Template copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      {/* Category Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'video', label: 'Video Ad Scripts', icon: Film },
          { id: 'copy', label: 'Ad Copy', icon: PenTool },
          { id: 'landing', label: 'Landing Pages', icon: Layout },
        ].map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={cn(
              "px-4 py-2 rounded-lg flex items-center gap-2 transition-all",
              activeCategory === cat.id
                ? "bg-primary text-black font-medium"
                : "glass border border-white/10 hover:border-primary/50"
            )}
          >
            <cat.icon className="w-4 h-4" />
            {cat.label}
          </button>
        ))}
      </div>

      {/* Video Ad Templates */}
      {activeCategory === 'video' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CINEMATIC_TEMPLATES.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl p-5 border border-white/10 hover:border-cyan-500/50 transition-all cursor-pointer"
              onClick={() => setSelectedTemplate(template)}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Film className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white">{template.name}</h3>
                  <p className="text-xs text-gray-400">{template.duration}</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 mb-3">{template.description}</p>
              <div className="flex items-center gap-2 text-cyan-400 text-sm">
                <span>View Template</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Ad Copy Templates */}
      {activeCategory === 'copy' && (
        <div className="grid md:grid-cols-2 gap-4">
          {AD_COPY_TEMPLATES.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl p-5 border border-white/10"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <PenTool className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{template.name}</h3>
                    <p className="text-xs text-gray-400">{template.description}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyTemplate(template.template)}
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <pre className="text-xs text-gray-400 bg-black/30 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                {template.template}
              </pre>
            </motion.div>
          ))}
        </div>
      )}

      {/* Landing Page Templates */}
      {activeCategory === 'landing' && (
        <div className="grid md:grid-cols-3 gap-4">
          {LANDING_PAGE_TEMPLATES.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl p-5 border border-white/10"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Layout className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="font-medium text-white">{template.name}</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyTemplate(template.heroTemplate)}
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-sm text-gray-400 mb-3">{template.description}</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {template.sections.map((section) => (
                  <span key={section} className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400">
                    {section}
                  </span>
                ))}
              </div>
              <pre className="text-xs text-gray-400 bg-black/30 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                {template.heroTemplate}
              </pre>
            </motion.div>
          ))}
        </div>
      )}

      {/* Video Template Detail Modal */}
      <AnimatePresence>
        {selectedTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedTemplate(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card rounded-2xl border border-white/10 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-heading font-bold text-white">{selectedTemplate.name}</h2>
                  <p className="text-gray-400">{selectedTemplate.duration} • {selectedTemplate.description}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedTemplate(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Script Structure */}
              <div className="mb-6">
                <h3 className="font-semibold text-white mb-3">Script Structure</h3>
                <div className="space-y-2">
                  {selectedTemplate.structure.map((step, i) => (
                    <div key={i} className="flex gap-3 p-3 rounded-lg bg-white/5">
                      <span className="text-cyan-400 font-mono text-sm shrink-0">{step.time}</span>
                      <div>
                        <span className="text-white font-medium">{step.section}</span>
                        <p className="text-gray-400 text-sm">{step.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Headlines */}
              <div className="mb-6">
                <h3 className="font-semibold text-white mb-3">Headline Examples</h3>
                <div className="space-y-2">
                  {selectedTemplate.headlines.map((headline, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <span className="text-gray-300">{headline}</span>
                      <Button variant="ghost" size="sm" onClick={() => copyTemplate(headline)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div>
                <h3 className="font-semibold text-white mb-3">CTA Examples</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate.ctaExamples.map((cta, i) => (
                    <button
                      key={i}
                      onClick={() => copyTemplate(cta)}
                      className="px-4 py-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-all"
                    >
                      {cta}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ BEST OF BEST CREATOR TOOLS ============

// Post Card Creator - Full featured tool
function PostCardCreator() {
  const [frontContent, setFrontContent] = useState({
    headline: '',
    subheadline: '',
    image: '',
    callToAction: '',
  });
  const [backContent, setBackContent] = useState({
    message: '',
    senderName: '',
    senderAddress: '',
    recipientPlaceholder: 'Recipient Address Here',
  });
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);

  // Standard USPS postcard dimensions
  const POSTCARD_SPECS = {
    width: '6 inches',
    height: '4.25 inches',
    bleed: '0.125 inches',
    safeZone: '0.25 inches from edge',
    stampArea: '1.5" x 1" (top right corner)',
    addressArea: '4" x 2.5" (right half of back)',
    returnAddress: '2" x 1" (top left corner of back)',
  };

  const handleGenerateFront = async () => {
    if (!frontContent.headline) {
      toast.error('Please enter a headline');
      return;
    }
    setLoading(true);
    try {
      const response = await api.generateCopy({
        prompt: `Create compelling postcard front content for a direct mail campaign:
Headline: ${frontContent.headline}
Subheadline: ${frontContent.subheadline || 'Generate one'}
Call to Action: ${frontContent.callToAction || 'Generate one'}

Provide:
1. An attention-grabbing headline (max 8 words)
2. A compelling subheadline (max 15 words)
3. A clear call-to-action
4. Image suggestion for the visual`,
        style: 'direct_mail'
      });
      setGeneratedContent({ ...generatedContent, front: response.content });
      toast.success('Front content generated!');
    } catch (error) {
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBack = async () => {
    setLoading(true);
    try {
      const response = await api.generateCopy({
        prompt: `Create postcard back message content:
Main message for the recipient (100-150 words max)
Make it personal, compelling, and include a clear benefit statement.
Headline context: ${frontContent.headline}`,
        style: 'direct_mail'
      });
      setBackContent({ ...backContent, message: response.content });
      toast.success('Back message generated!');
    } catch (error) {
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
          <Mail className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Post Card Creator</h2>
          <p className="text-sm text-gray-400">Design front & back with USPS-compliant dimensions</p>
        </div>
      </div>

      {/* Specifications Card */}
      <div className="glass rounded-xl p-4 border border-amber-500/20">
        <h3 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          USPS Standard Postcard Specs
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          {Object.entries(POSTCARD_SPECS).map(([key, value]) => (
            <div key={key} className="bg-black/30 rounded-lg p-2">
              <p className="text-gray-500 text-xs capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
              <p className="text-white font-medium">{value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Front Side */}
        <div className="glass rounded-xl p-5">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">FRONT</span>
            Visual Side
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Headline *</label>
              <Input
                value={frontContent.headline}
                onChange={(e) => setFrontContent({ ...frontContent, headline: e.target.value })}
                placeholder="e.g., Limited Time Offer!"
                className="bg-black/30 border-white/10"
              />
            </div>
            
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Subheadline</label>
              <Input
                value={frontContent.subheadline}
                onChange={(e) => setFrontContent({ ...frontContent, subheadline: e.target.value })}
                placeholder="e.g., Save 20% on your first order"
                className="bg-black/30 border-white/10"
              />
            </div>
            
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Call to Action</label>
              <Input
                value={frontContent.callToAction}
                onChange={(e) => setFrontContent({ ...frontContent, callToAction: e.target.value })}
                placeholder="e.g., Visit example.com/offer"
                className="bg-black/30 border-white/10"
              />
            </div>

            <Button onClick={handleGenerateFront} disabled={loading} className="w-full btn-primary">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate Front Content
            </Button>

            {/* Preview */}
            <div className="aspect-[6/4.25] bg-gradient-to-br from-amber-900/30 to-orange-900/30 rounded-xl border-2 border-dashed border-amber-500/30 flex flex-col items-center justify-center p-4 text-center">
              <p className="text-lg font-bold text-white mb-1">{frontContent.headline || 'Your Headline Here'}</p>
              <p className="text-sm text-gray-300 mb-3">{frontContent.subheadline || 'Subheadline goes here'}</p>
              <div className="px-4 py-2 bg-amber-500 text-black rounded-lg font-semibold text-sm">
                {frontContent.callToAction || 'Call to Action'}
              </div>
              <p className="text-xs text-gray-500 mt-4">6&quot; x 4.25&quot; Preview</p>
            </div>
          </div>
        </div>

        {/* Back Side */}
        <div className="glass rounded-xl p-5">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-xs">BACK</span>
            Mailing Side
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Message (Left Side)</label>
              <Textarea
                value={backContent.message}
                onChange={(e) => setBackContent({ ...backContent, message: e.target.value })}
                placeholder="Your personal message to the recipient..."
                rows={4}
                className="bg-black/30 border-white/10"
              />
            </div>
            
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Return Address (Top Left)</label>
              <Textarea
                value={backContent.senderAddress}
                onChange={(e) => setBackContent({ ...backContent, senderAddress: e.target.value })}
                placeholder="Your Business Name&#10;123 Main Street&#10;City, ST 12345"
                rows={3}
                className="bg-black/30 border-white/10"
              />
            </div>

            <Button onClick={handleGenerateBack} disabled={loading} className="w-full" variant="outline">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Generate Message
            </Button>

            {/* Back Preview */}
            <div className="aspect-[6/4.25] bg-white rounded-xl border border-gray-200 relative overflow-hidden">
              {/* Left half - message */}
              <div className="absolute left-0 top-0 bottom-0 w-1/2 p-3 border-r border-gray-300">
                <p className="text-xs text-gray-800 leading-relaxed">
                  {backContent.message || 'Your personalized message will appear here...'}
                </p>
              </div>
              {/* Right half - address area */}
              <div className="absolute right-0 top-0 bottom-0 w-1/2 p-3">
                {/* Return address */}
                <div className="text-xs text-gray-600 mb-6">
                  {backContent.senderAddress || 'Return Address'}
                </div>
                {/* Stamp area */}
                <div className="absolute top-2 right-2 w-12 h-10 border border-gray-300 rounded flex items-center justify-center">
                  <span className="text-xs text-gray-400">STAMP</span>
                </div>
                {/* Recipient address */}
                <div className="absolute bottom-8 right-4 left-auto w-3/4">
                  <div className="border-b border-gray-300 mb-2 pb-1 text-sm text-gray-400">Name</div>
                  <div className="border-b border-gray-300 mb-2 pb-1 text-sm text-gray-400">Street Address</div>
                  <div className="border-b border-gray-300 pb-1 text-sm text-gray-400">City, State ZIP</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {generatedContent?.front && (
        <div className="glass rounded-xl p-4 border border-green-500/20">
          <h4 className="font-semibold text-green-400 mb-2">Generated Content</h4>
          <pre className="text-sm text-gray-300 whitespace-pre-wrap">{generatedContent.front}</pre>
        </div>
      )}
    </div>
  );
}

// TV Commercial Creator
function TVCommercialCreator() {
  const [formData, setFormData] = useState({
    productName: '',
    duration: '30',
    tone: 'professional',
    targetAudience: '',
    keyMessage: '',
  });
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState('');

  const durations = [
    { value: '15', label: '15 seconds' },
    { value: '30', label: '30 seconds' },
    { value: '60', label: '60 seconds' },
  ];

  const handleGenerate = async () => {
    if (!formData.productName || !formData.keyMessage) {
      toast.error('Please fill in product name and key message');
      return;
    }
    setLoading(true);
    try {
      const response = await api.generateCopy({
        prompt: `Create a ${formData.duration}-second TV commercial script for:
Product/Service: ${formData.productName}
Target Audience: ${formData.targetAudience}
Key Message: ${formData.keyMessage}
Tone: ${formData.tone}

Include:
1. VISUAL descriptions for each scene
2. VOICEOVER/DIALOGUE text
3. TIMING for each section
4. Call-to-action at the end
5. Music/sound suggestions`,
        style: 'tv_commercial'
      });
      setScript(response.content);
      toast.success('TV commercial script generated!');
    } catch (error) {
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Tv className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">TV Commercial Creator</h2>
          <p className="text-sm text-gray-400">Script and storyboard for broadcast TV spots</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Product/Service Name *</label>
            <Input
              value={formData.productName}
              onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
              placeholder="e.g., XYZ Insurance"
              className="bg-black/30 border-white/10"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Duration</label>
            <div className="flex gap-2">
              {durations.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setFormData({ ...formData, duration: d.value })}
                  className={cn(
                    "flex-1 px-3 py-2 rounded-lg text-sm transition-all",
                    formData.duration === d.value
                      ? "bg-amber-500/20 text-amber-400 border border-amber-500/50"
                      : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Target Audience</label>
            <Input
              value={formData.targetAudience}
              onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
              placeholder="e.g., Families with young children"
              className="bg-black/30 border-white/10"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Key Message *</label>
            <Textarea
              value={formData.keyMessage}
              onChange={(e) => setFormData({ ...formData, keyMessage: e.target.value })}
              placeholder="What's the main takeaway for viewers?"
              rows={3}
              className="bg-black/30 border-white/10"
            />
          </div>

          <Button onClick={handleGenerate} disabled={loading} className="w-full btn-primary">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
            Generate TV Script
          </Button>
        </div>

        <div className="glass rounded-xl p-4">
          <h3 className="font-semibold text-white mb-3">Generated Script</h3>
          {script ? (
            <pre className="text-sm text-gray-300 whitespace-pre-wrap bg-black/30 rounded-lg p-4 max-h-96 overflow-auto">
              {script}
            </pre>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <Tv className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Your TV commercial script will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Radio Ad Creator
function RadioAdCreator() {
  const [formData, setFormData] = useState({
    productName: '',
    duration: '30',
    style: 'conversational',
    keyPoints: '',
  });
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState('');

  const styles = [
    { value: 'conversational', label: 'Conversational' },
    { value: 'energetic', label: 'Energetic' },
    { value: 'authoritative', label: 'Authoritative' },
    { value: 'humorous', label: 'Humorous' },
  ];

  const handleGenerate = async () => {
    if (!formData.productName) {
      toast.error('Please enter product name');
      return;
    }
    setLoading(true);
    try {
      const response = await api.generateCopy({
        prompt: `Create a ${formData.duration}-second radio advertisement script:
Product/Service: ${formData.productName}
Style: ${formData.style}
Key Points: ${formData.keyPoints}

Include:
1. Hook (first 5 seconds)
2. Main message
3. Benefits/features
4. Call to action with contact info placeholder
5. Word count estimate
6. Suggested voice talent type`,
        style: 'radio_ad'
      });
      setScript(response.content);
      toast.success('Radio ad script generated!');
    } catch (error) {
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center">
          <Radio className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Radio Ad Creator</h2>
          <p className="text-sm text-gray-400">Audio scripts optimized for radio broadcast</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Product/Service Name *</label>
            <Input
              value={formData.productName}
              onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
              placeholder="e.g., Joe's Auto Repair"
              className="bg-black/30 border-white/10"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Duration</label>
            <div className="flex gap-2">
              {['15', '30', '60'].map((d) => (
                <button
                  key={d}
                  onClick={() => setFormData({ ...formData, duration: d })}
                  className={cn(
                    "flex-1 px-3 py-2 rounded-lg text-sm transition-all",
                    formData.duration === d
                      ? "bg-green-500/20 text-green-400 border border-green-500/50"
                      : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                  )}
                >
                  {d}s
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Voice Style</label>
            <div className="grid grid-cols-2 gap-2">
              {styles.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setFormData({ ...formData, style: s.value })}
                  className={cn(
                    "px-3 py-2 rounded-lg text-sm transition-all",
                    formData.style === s.value
                      ? "bg-green-500/20 text-green-400 border border-green-500/50"
                      : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Key Points to Include</label>
            <Textarea
              value={formData.keyPoints}
              onChange={(e) => setFormData({ ...formData, keyPoints: e.target.value })}
              placeholder="List the main points, offers, or features..."
              rows={3}
              className="bg-black/30 border-white/10"
            />
          </div>

          <Button onClick={handleGenerate} disabled={loading} className="w-full btn-primary">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4 mr-2" />}
            Generate Radio Script
          </Button>
        </div>

        <div className="glass rounded-xl p-4">
          <h3 className="font-semibold text-white mb-3">Generated Script</h3>
          {script ? (
            <pre className="text-sm text-gray-300 whitespace-pre-wrap bg-black/30 rounded-lg p-4 max-h-96 overflow-auto">
              {script}
            </pre>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <Radio className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Your radio ad script will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Digital Billboard Creator
function BillboardCreator() {
  const [formData, setFormData] = useState({
    headline: '',
    subtext: '',
    size: '14x48',
    displayTime: '8',
  });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState('');

  const sizes = [
    { value: '14x48', label: '14\' x 48\' (Bulletin)' },
    { value: '10.5x36', label: '10.5\' x 36\' (Poster)' },
    { value: '5x11', label: '5\' x 11\' (Junior)' },
  ];

  const handleGenerate = async () => {
    if (!formData.headline) {
      toast.error('Please enter a headline');
      return;
    }
    setLoading(true);
    try {
      const response = await api.generateCopy({
        prompt: `Create digital billboard content for:
Headline: ${formData.headline}
Size: ${formData.size} feet
Display time: ${formData.displayTime} seconds

Billboard best practices:
- Maximum 7 words for headline
- Readable at 500+ feet
- High contrast colors
- Simple, bold imagery

Provide:
1. Optimized headline (7 words max)
2. Optional subtext (3-4 words max)
3. Color scheme recommendations
4. Visual element suggestions
5. Font recommendations`,
        style: 'billboard'
      });
      setSuggestions(response.content);
      toast.success('Billboard content generated!');
    } catch (error) {
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
          <Monitor className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Digital Billboard Creator</h2>
          <p className="text-sm text-gray-400">High-impact designs for outdoor digital displays</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Main Headline (7 words max) *</label>
            <Input
              value={formData.headline}
              onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
              placeholder="e.g., Fresh Coffee. Drive Thru Open."
              className="bg-black/30 border-white/10"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Subtext (optional)</label>
            <Input
              value={formData.subtext}
              onChange={(e) => setFormData({ ...formData, subtext: e.target.value })}
              placeholder="e.g., Exit 42"
              className="bg-black/30 border-white/10"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Billboard Size</label>
            <div className="space-y-2">
              {sizes.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setFormData({ ...formData, size: s.value })}
                  className={cn(
                    "w-full px-3 py-2 rounded-lg text-sm text-left transition-all",
                    formData.size === s.value
                      ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                      : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={handleGenerate} disabled={loading} className="w-full btn-primary">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate Billboard Content
          </Button>

          {/* Preview */}
          <div className="aspect-[48/14] bg-gradient-to-r from-blue-900 to-indigo-900 rounded-xl border border-blue-500/30 flex flex-col items-center justify-center p-4 text-center">
            <p className="text-2xl font-bold text-white mb-1">{formData.headline || 'YOUR HEADLINE'}</p>
            {formData.subtext && <p className="text-lg text-blue-300">{formData.subtext}</p>}
            <p className="text-xs text-gray-500 mt-3">Preview: {formData.size} Billboard</p>
          </div>
        </div>

        <div className="glass rounded-xl p-4">
          <h3 className="font-semibold text-white mb-3">Design Suggestions</h3>
          {suggestions ? (
            <pre className="text-sm text-gray-300 whitespace-pre-wrap bg-black/30 rounded-lg p-4 max-h-96 overflow-auto">
              {suggestions}
            </pre>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <Monitor className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Billboard design suggestions will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Project History Component
function ProjectHistory() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [folders, setFolders] = useState([]);
  const [viewProject, setViewProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  useEffect(() => {
    loadProjects();
    loadFolders();
  }, []);
  
  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/diy/projects`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadFolders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/diy/folders`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders || []);
      }
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };
  
  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      const response = await fetch(`${API_URL}/api/diy/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        toast.success('Project deleted');
        setProjects(projects.filter(p => p.id !== projectId));
      }
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };
  
  const updateProject = async (projectId, data) => {
    try {
      const response = await fetch(`${API_URL}/api/diy/projects/${projectId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        toast.success('Project updated');
        loadProjects();
        setEditingProject(null);
      }
    } catch (error) {
      toast.error('Failed to update project');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };
  
  const getToolIcon = (tool) => {
    const icons = {
      'app_builder': Code2,
      'copywriter': FileText,
      'image': Image,
      'rewriter': RefreshCw,
      'adbuilder': Megaphone,
      'default': Sparkles
    };
    return icons[tool] || icons.default;
  };
  
  // Filter projects
  const filteredProjects = projects.filter(p => {
    const matchesSearch = !searchQuery || 
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.tool?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.project_type?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFolder = !selectedFolder || p.folder === selectedFolder;
    return matchesSearch && matchesFolder;
  });
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      data-testid="project-history"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-cyan-400" />
            My Projects
          </h2>
          <p className="text-gray-400 text-sm mt-1">View and manage your saved AI tool projects</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
            {projects.length} Projects
          </span>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search projects..."
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          />
        </div>
        
        {folders.length > 0 && (
          <select
            value={selectedFolder}
            onChange={(e) => setSelectedFolder(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          >
            <option value="">All Folders</option>
            {folders.map(folder => (
              <option key={folder} value={folder}>{folder}</option>
            ))}
          </select>
        )}
      </div>
      
      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
          <FolderOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {searchQuery || selectedFolder ? 'No matching projects' : 'No projects yet'}
          </h3>
          <p className="text-gray-400">
            {searchQuery || selectedFolder 
              ? 'Try adjusting your search or filters'
              : 'Projects you create with DIY tools will appear here'}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project, index) => {
            const ToolIcon = getToolIcon(project.tool);
            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-card rounded-xl border border-white/10 p-4 hover:border-cyan-500/30 transition-all group"
                data-testid={`project-card-${project.id}`}
              >
                {/* Project Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
                      <ToolIcon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white line-clamp-1">
                        {project.name || project.project_type || 'Untitled Project'}
                      </h3>
                      <p className="text-xs text-gray-500 capitalize">{project.tool?.replace('_', ' ') || 'DIY Tool'}</p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setViewProject(project)}
                      className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                      title="View"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingProject(project)}
                      className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteProject(project.id)}
                      className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Preview Content */}
                {project.preview_content && (
                  <div className="mb-3 p-3 bg-black/30 rounded-lg max-h-24 overflow-hidden">
                    <p className="text-xs text-gray-400 line-clamp-3 whitespace-pre-wrap">
                      {typeof project.preview_content === 'string' 
                        ? project.preview_content.substring(0, 200) 
                        : JSON.stringify(project.preview_content).substring(0, 200)}...
                    </p>
                  </div>
                )}
                
                {/* Meta Info */}
                <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-white/5">
                  <div className="flex items-center gap-3">
                    {project.folder && (
                      <span className="flex items-center gap-1">
                        <FolderOpen className="w-3 h-3" />
                        {project.folder}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(project.created_at)}
                    </span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                    project.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    project.status === 'draft' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {project.status || 'draft'}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      
      {/* View Project Modal */}
      <AnimatePresence>
        {viewProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setViewProject(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-card rounded-2xl border border-white/10 max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">
                  {viewProject.name || viewProject.project_type || 'Project Details'}
                </h3>
                <button onClick={() => setViewProject(null)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500">Tool</label>
                    <p className="text-white capitalize">{viewProject.tool?.replace('_', ' ') || viewProject.tool?.replace('-', ' ')}</p>
                  </div>
                  {viewProject.project_type && (
                    <div>
                      <label className="text-xs text-gray-500">Type</label>
                      <p className="text-white capitalize">{viewProject.project_type?.replace('_', ' ')}</p>
                    </div>
                  )}
                  
                  {/* Ad Generator Project Details */}
                  {(viewProject.tool === 'ad-generator' || viewProject.ads) && (
                    <>
                      {viewProject.business_name && (
                        <div>
                          <label className="text-xs text-gray-500">Business</label>
                          <p className="text-white">{viewProject.business_name}</p>
                        </div>
                      )}
                      <div className="flex gap-4">
                        {viewProject.platform && (
                          <div>
                            <label className="text-xs text-gray-500">Platform</label>
                            <p className="text-white capitalize">{viewProject.platform}</p>
                          </div>
                        )}
                        {viewProject.tone && (
                          <div>
                            <label className="text-xs text-gray-500">Tone</label>
                            <p className="text-white capitalize">{viewProject.tone}</p>
                          </div>
                        )}
                        {viewProject.goal && (
                          <div>
                            <label className="text-xs text-gray-500">Goal</label>
                            <p className="text-white capitalize">{viewProject.goal}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Video */}
                      {viewProject.video_url && (
                        <div>
                          <label className="text-xs text-gray-500 mb-2 block">Generated Video</label>
                          <div className="rounded-xl overflow-hidden border border-white/10 mb-2">
                            <video 
                              src={viewProject.video_url} 
                              controls 
                              className="w-full max-h-[300px] object-contain bg-black"
                            />
                          </div>
                          <a
                            href={viewProject.video_url}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors"
                          >
                            <Download className="w-4 h-4" /> Download Video
                          </a>
                        </div>
                      )}
                      
                      {/* Generated Ads */}
                      {viewProject.ads && viewProject.ads.length > 0 && (
                        <div>
                          <label className="text-xs text-gray-500 mb-2 block">Generated Ads ({viewProject.ads.length})</label>
                          <div className="space-y-3">
                            {viewProject.ads.map((ad, idx) => (
                              <div key={idx} className="p-4 bg-black/30 rounded-lg border border-white/10">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                    Ad {idx + 1}
                                  </span>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(`${ad.headline}\n\n${ad.body}\n\n${ad.cta}`);
                                      toast.success('Ad copied!');
                                    }}
                                    className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                                
                                {/* Image for this ad */}
                                {viewProject.image_urls && viewProject.image_urls[idx] && (
                                  <div className="mb-3">
                                    <img 
                                      src={viewProject.image_urls[idx]} 
                                      alt={`Ad ${idx + 1}`}
                                      className="w-full max-h-[200px] object-contain rounded-lg border border-white/10"
                                    />
                                    <a
                                      href={viewProject.image_urls[idx]}
                                      download
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-1 mt-2 bg-pink-500/20 text-pink-400 rounded text-xs hover:bg-pink-500/30 transition-colors"
                                    >
                                      <Download className="w-3 h-3" /> Download
                                    </a>
                                  </div>
                                )}
                                
                                <div className="space-y-2">
                                  <div>
                                    <span className="text-[10px] text-gray-500 uppercase">Headline</span>
                                    <p className="text-white font-semibold">{ad.headline}</p>
                                  </div>
                                  <div>
                                    <span className="text-[10px] text-gray-500 uppercase">Body</span>
                                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{ad.body}</p>
                                  </div>
                                  <div>
                                    <span className="text-[10px] text-gray-500 uppercase">CTA</span>
                                    <span className="inline-block px-3 py-1 bg-purple-500 text-white rounded text-sm mt-1">
                                      {ad.cta}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div>
                    <label className="text-xs text-gray-500">Created</label>
                    <p className="text-white">{formatDate(viewProject.created_at)}</p>
                  </div>
                  {viewProject.preview_content && (
                    <div>
                      <label className="text-xs text-gray-500">Content</label>
                      <div className="mt-2 p-4 bg-black/30 rounded-lg">
                        <pre className="text-sm text-gray-300 whitespace-pre-wrap">
                          {typeof viewProject.preview_content === 'string' 
                            ? viewProject.preview_content 
                            : JSON.stringify(viewProject.preview_content, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Edit Project Modal */}
      <AnimatePresence>
        {editingProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setEditingProject(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-card rounded-2xl border border-white/10 max-w-md w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Edit Project</h3>
                <button onClick={() => setEditingProject(null)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                updateProject(editingProject.id, {
                  name: formData.get('name'),
                  folder: formData.get('folder'),
                  notes: formData.get('notes')
                });
              }} className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Project Name</label>
                  <input
                    name="name"
                    defaultValue={editingProject.name || ''}
                    placeholder="Enter project name"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Folder</label>
                  <input
                    name="folder"
                    defaultValue={editingProject.folder || ''}
                    placeholder="e.g., Marketing, Sales"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Notes</label>
                  <textarea
                    name="notes"
                    defaultValue={editingProject.notes || ''}
                    placeholder="Add notes about this project..."
                    rows={3}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setEditingProject(null)}
                    className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ==================== AD TOOLS SUITE ====================

// Ad Generator Tool - Generate high-converting ads with images/videos
function AdGeneratorTool({ embedded = false }) {
  const [formData, setFormData] = useState({
    business_name: '',
    industry: '',
    product_service: '',
    target_audience: '',
    unique_value: '',
    tone: 'professional',
    platform: 'facebook',
    goal: 'conversions'
  });
  const [loading, setLoading] = useState(false);
  const [generatedAds, setGeneratedAds] = useState(null);
  const [copied, setCopied] = useState({});
  
  // Media generation state
  const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
  const [imageStyle, setImageStyle] = useState('cinematic');
  const [imageSize, setImageSize] = useState('1024x1024');
  const [videoSize, setVideoSize] = useState('1280x720');
  const [videoDuration, setVideoDuration] = useState(8);
  const [generatingMedia, setGeneratingMedia] = useState({});
  const [adImages, setAdImages] = useState({});
  const [adVideo, setAdVideo] = useState(null);
  const [savingProject, setSavingProject] = useState(false);
  const [projectSaved, setProjectSaved] = useState(false);
  
  // NEW: Logo upload and video options
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [includeVoiceover, setIncludeVoiceover] = useState(false);
  const [includeBackgroundMusic, setIncludeBackgroundMusic] = useState(true);
  
  // NEW: Custom prompt for image/video generation
  const [customImagePrompt, setCustomImagePrompt] = useState('');
  const [customVideoPrompt, setCustomVideoPrompt] = useState('');
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  
  // Auto-save: Track current project ID for updates
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  
  const token = localStorage.getItem('token');

  // Handle logo upload - increased to 20MB
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (20MB max)
      if (file.size > 20 * 1024 * 1024) {
        toast.error('Logo file size must be under 20MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const platforms = [
    { id: 'facebook', label: 'Facebook', icon: '📘' },
    { id: 'instagram', label: 'Instagram', icon: '📸' },
    { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { id: 'google', label: 'Google Ads', icon: '🔍' },
    { id: 'tiktok', label: 'TikTok', icon: '🎵' },
    { id: 'twitter', label: 'X/Twitter', icon: '🐦' },
  ];

  const tones = [
    { id: 'professional', label: 'Professional' },
    { id: 'casual', label: 'Casual & Friendly' },
    { id: 'urgent', label: 'Urgent & Bold' },
    { id: 'emotional', label: 'Emotional' },
    { id: 'humorous', label: 'Humorous' },
    { id: 'luxury', label: 'Luxury & Premium' },
  ];

  const goals = [
    { id: 'conversions', label: 'Drive Conversions' },
    { id: 'awareness', label: 'Brand Awareness' },
    { id: 'engagement', label: 'Engagement' },
    { id: 'traffic', label: 'Website Traffic' },
    { id: 'leads', label: 'Lead Generation' },
  ];

  // Styles with Cinematic FIRST per user request
  const imageStyles = [
    { id: 'cinematic', label: '🎬 Cinematic', desc: 'Dramatic & epic' },
    { id: 'realistic', label: '📷 Realistic', desc: 'Photo-quality visuals' },
    { id: 'artistic', label: '🎨 Artistic', desc: 'Creative & colorful' },
    { id: 'minimalist', label: '⬜ Minimalist', desc: 'Clean & simple' },
    { id: 'bold', label: '⚡ Bold', desc: 'High contrast & impactful' },
  ];

  // Image sizes: Square, Landscape, Mobile
  const imageSizes = [
    { id: '1024x1024', label: 'Square', desc: '1:1', icon: '⬜' },
    { id: '1792x1024', label: 'Landscape', desc: '16:9', icon: '🖼️' },
    { id: '1024x1792', label: 'Mobile', desc: '9:16', icon: '📱' },
  ];

  // Video sizes: Landscape, Mobile
  const videoSizes = [
    { id: '1280x720', label: 'Landscape', desc: '16:9 HD', icon: '🖥️' },
    { id: '1024x1792', label: 'Mobile', desc: '9:16 Portrait', icon: '📱' },
  ];

  const videoDurations = [
    { id: 4, label: '4 sec', desc: 'Quick bumper' },
    { id: 8, label: '8 sec', desc: 'Short ad' },
    { id: 12, label: '12 sec', desc: 'Standard ad' },
  ];

  const generateAds = async () => {
    if (!formData.business_name || !formData.product_service) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    setAdImages({});
    setAdVideo(null);
    setProjectSaved(false);
    // Reset project ID when generating completely new ads (fresh start)
    // But only if user hasn't saved anything yet
    if (!projectSaved) {
      setCurrentProjectId(null);
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diy/ad-generator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Generation failed');
      const data = await response.json();
      setGeneratedAds(data.ads);
      toast.success('Ads generated successfully!');
      
      // Auto-save after generating new ads
      autoSaveProject(data.ads, {}, null);
    } catch (error) {
      toast.error('Failed to generate ads');
    } finally {
      setLoading(false);
    }
  };

  const generateAdImage = async (adIndex, headline) => {
    setGeneratingMedia({ ...generatingMedia, [adIndex]: true });
    try {
      // Use custom prompt if provided, otherwise generate from form data
      let imagePrompt;
      if (useCustomPrompt && customImagePrompt.trim()) {
        imagePrompt = customImagePrompt;
      } else {
        imagePrompt = `${formData.product_service}, ${formData.industry || 'business'}, ${headline}, advertising visual, marketing`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diy/ad-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: imagePrompt,
          style: imageStyle,
          size: imageSize
        })
      });
      
      if (!response.ok) throw new Error('Image generation failed');
      const data = await response.json();
      const newImages = { ...adImages, [adIndex]: data.image_url };
      setAdImages(newImages);
      toast.success('Ad image generated!');
      
      // Auto-save after image generation
      if (generatedAds && generatedAds.length > 0) {
        autoSaveProject(generatedAds, newImages, adVideo);
      }
    } catch (error) {
      toast.error('Failed to generate image');
    } finally {
      setGeneratingMedia({ ...generatingMedia, [adIndex]: false });
    }
  };

  const generateAdVideo = async (headline) => {
    setGeneratingMedia({ ...generatingMedia, video: true });
    try {
      // Use custom prompt if provided, otherwise generate from form data
      let videoPrompt;
      if (useCustomPrompt && customVideoPrompt.trim()) {
        videoPrompt = customVideoPrompt;
      } else {
        videoPrompt = `${formData.product_service}, ${formData.industry || 'business'}, ${headline}, advertising video, cinematic`;
      }
      
      // Add voice-over and music instructions to prompt
      if (includeVoiceover) {
        videoPrompt += ', with professional voice-over narration';
      }
      if (includeBackgroundMusic) {
        videoPrompt += ', with fitting background music';
      }
      if (!includeVoiceover && !includeBackgroundMusic) {
        videoPrompt += ', no audio';
      }
      
      // Video generation can take 2-5 minutes, use AbortController with longer timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minute timeout
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diy/ad-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: videoPrompt,
          style: imageStyle,
          size: videoSize,
          duration: videoDuration,
          include_voiceover: includeVoiceover,
          include_music: includeBackgroundMusic
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Video generation failed');
      }
      const data = await response.json();
      setAdVideo(data.video_url);
      toast.success('Ad video generated successfully!');
      
      // Auto-save after video generation
      if (generatedAds && generatedAds.length > 0) {
        autoSaveProject(generatedAds, adImages, data.video_url);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error('Video generation timed out. Please try again.');
      } else {
        toast.error(`Failed to generate video: ${error.message}`);
      }
      console.error('Video generation error:', error);
    } finally {
      setGeneratingMedia({ ...generatingMedia, video: false });
    }
  };

  const saveProject = async () => {
    if (!generatedAds || generatedAds.length === 0) {
      toast.error('Generate ads first before saving');
      return;
    }

    setSavingProject(true);
    try {
      const projectName = `${formData.business_name} - ${formData.platform} Ads`;
      const projectData = {
        project_name: projectName,
        business_name: formData.business_name,
        platform: formData.platform,
        tone: formData.tone,
        goal: formData.goal,
        ads: generatedAds,
        image_urls: Object.keys(adImages).length > 0 ? adImages : null,
        video_url: adVideo,
        media_type: mediaType,
        media_size: mediaType === 'image' ? imageSize : videoSize
      };
      
      let response;
      if (currentProjectId) {
        // Update existing project
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diy/update-ad-project/${currentProjectId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(projectData)
        });
      } else {
        // Create new project
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diy/save-ad-project`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(projectData)
        });
      }
      
      if (!response.ok) throw new Error('Failed to save project');
      const data = await response.json();
      
      // Store project ID for future updates
      if (data.project_id) {
        setCurrentProjectId(data.project_id);
      }
      
      setProjectSaved(true);
      toast.success(currentProjectId ? 'Project updated!' : 'Project saved to My Projects!');
    } catch (error) {
      toast.error('Failed to save project');
    } finally {
      setSavingProject(false);
    }
  };
  
  // Auto-save function - silently saves/updates in background
  const autoSaveProject = async (newAds, newImages, newVideo) => {
    if (!newAds || newAds.length === 0) return;
    if (!formData.business_name) return;
    
    setAutoSaving(true);
    try {
      const projectName = `${formData.business_name} - ${formData.platform} Ads`;
      const projectData = {
        project_name: projectName,
        business_name: formData.business_name,
        platform: formData.platform,
        tone: formData.tone,
        goal: formData.goal,
        ads: newAds,
        image_urls: Object.keys(newImages || {}).length > 0 ? newImages : null,
        video_url: newVideo || null,
        media_type: mediaType,
        media_size: mediaType === 'image' ? imageSize : videoSize
      };
      
      let response;
      if (currentProjectId) {
        // Update existing project
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diy/update-ad-project/${currentProjectId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(projectData)
        });
      } else {
        // Create new project
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diy/save-ad-project`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(projectData)
        });
      }
      
      if (response.ok) {
        const data = await response.json();
        if (data.project_id && !currentProjectId) {
          setCurrentProjectId(data.project_id);
        }
        setProjectSaved(true);
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
    toast.success('Copied to clipboard!');
  };

  // Calculate aspect ratio class for display
  const getMediaAspectClass = () => {
    const size = mediaType === 'image' ? imageSize : videoSize;
    if (size === '1024x1024') return 'aspect-square';
    if (size === '1792x1024' || size === '1280x720') return 'aspect-video';
    if (size === '1024x1792') return 'aspect-[9/16]';
    return 'aspect-video';
  };

  return (
    <div className="space-y-6">
      <div className={cn(
        "glass rounded-2xl p-6 border",
        embedded ? "border-purple-500/50 bg-gradient-to-br from-purple-900/20 to-violet-900/20" : "border-purple-500/30"
      )}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">AI Ad Generator</h2>
            <p className="text-gray-400">Generate high-converting ads with AI-powered images & videos</p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm text-gray-400">Business Name *</label>
            <Input
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              placeholder="Your Business Name"
              className="bg-black/30 border-white/10"
              data-testid="ad-gen-business-name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-gray-400">Industry</label>
            <Input
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              placeholder="e.g., Real Estate, Fitness, SaaS"
              className="bg-black/30 border-white/10"
              data-testid="ad-gen-industry"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="text-sm text-gray-400">Product/Service *</label>
            <Textarea
              value={formData.product_service}
              onChange={(e) => setFormData({ ...formData, product_service: e.target.value })}
              placeholder="Describe what you're selling or promoting..."
              rows={2}
              className="bg-black/30 border-white/10"
              data-testid="ad-gen-product"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="text-sm text-gray-400">Target Audience</label>
            <Input
              value={formData.target_audience}
              onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
              placeholder="e.g., Small business owners aged 35-55"
              className="bg-black/30 border-white/10"
              data-testid="ad-gen-audience"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="text-sm text-gray-400">Unique Value Proposition</label>
            <Input
              value={formData.unique_value}
              onChange={(e) => setFormData({ ...formData, unique_value: e.target.value })}
              placeholder="What makes you different from competitors?"
              className="bg-black/30 border-white/10"
              data-testid="ad-gen-value"
            />
          </div>
        </div>

        {/* Platform Selection */}
        <div className="mt-6">
          <label className="text-sm text-gray-400 block mb-3">Platform</label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {platforms.map((p) => (
              <button
                key={p.id}
                onClick={() => setFormData({ ...formData, platform: p.id })}
                className={cn(
                  "p-3 rounded-xl text-center transition-all",
                  formData.platform === p.id
                    ? "bg-purple-500/20 border border-purple-500/50"
                    : "bg-white/5 border border-white/10 hover:border-white/30"
                )}
                data-testid={`ad-gen-platform-${p.id}`}
              >
                <span className="text-xl block mb-1">{p.icon}</span>
                <span className="text-xs text-gray-400">{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tone Selection */}
        <div className="mt-6">
          <label className="text-sm text-gray-400 block mb-3">Tone</label>
          <div className="flex flex-wrap gap-2">
            {tones.map((t) => (
              <button
                key={t.id}
                onClick={() => setFormData({ ...formData, tone: t.id })}
                className={cn(
                  "px-4 py-2 rounded-full text-sm transition-all",
                  formData.tone === t.id
                    ? "bg-purple-500/20 text-purple-400 border border-purple-500/50"
                    : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/30"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Goal Selection */}
        <div className="mt-6">
          <label className="text-sm text-gray-400 block mb-3">Campaign Goal</label>
          <div className="flex flex-wrap gap-2">
            {goals.map((g) => (
              <button
                key={g.id}
                onClick={() => setFormData({ ...formData, goal: g.id })}
                className={cn(
                  "px-4 py-2 rounded-full text-sm transition-all",
                  formData.goal === g.id
                    ? "bg-purple-500/20 text-purple-400 border border-purple-500/50"
                    : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/30"
                )}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* Media Type Selection */}
        <div className="mt-6">
          <label className="text-sm text-gray-400 block mb-3">Output Type</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setMediaType('image')}
              className={cn(
                "p-4 rounded-xl text-center transition-all flex items-center justify-center gap-3",
                mediaType === 'image'
                  ? "bg-gradient-to-br from-pink-500/20 to-purple-500/20 border-2 border-pink-500/50"
                  : "bg-white/5 border border-white/10 hover:border-white/30"
              )}
              data-testid="ad-gen-type-image"
            >
              <Image className={cn("w-6 h-6", mediaType === 'image' ? "text-pink-400" : "text-gray-400")} />
              <div className="text-left">
                <span className={cn("font-medium block", mediaType === 'image' ? "text-pink-400" : "text-white")}>Image</span>
                <span className="text-xs text-gray-500">Nano Banana AI</span>
              </div>
            </button>
            <button
              onClick={() => setMediaType('video')}
              className={cn(
                "p-4 rounded-xl text-center transition-all flex items-center justify-center gap-3",
                mediaType === 'video'
                  ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-2 border-cyan-500/50"
                  : "bg-white/5 border border-white/10 hover:border-white/30"
              )}
              data-testid="ad-gen-type-video"
            >
              <Video className={cn("w-6 h-6", mediaType === 'video' ? "text-cyan-400" : "text-gray-400")} />
              <div className="text-left">
                <span className={cn("font-medium block", mediaType === 'video' ? "text-cyan-400" : "text-white")}>Video</span>
                <span className="text-xs text-gray-500">Sora 2 AI</span>
              </div>
            </button>
          </div>
        </div>

        {/* Style Selection (applies to both) */}
        <div className="mt-6">
          <label className="text-sm text-gray-400 block mb-3">Visual Style</label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {imageStyles.map((style) => (
              <button
                key={style.id}
                onClick={() => setImageStyle(style.id)}
                className={cn(
                  "p-3 rounded-xl text-center transition-all",
                  imageStyle === style.id
                    ? "bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/50"
                    : "bg-white/5 border border-white/10 hover:border-white/30"
                )}
                data-testid={`ad-gen-style-${style.id}`}
              >
                <span className="text-lg block mb-1">{style.label.split(' ')[0]}</span>
                <span className="text-xs text-gray-400">{style.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Size Selection based on media type */}
        <div className="mt-6">
          <label className="text-sm text-gray-400 block mb-3">
            {mediaType === 'image' ? 'Image Size' : 'Video Size'}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(mediaType === 'image' ? imageSizes : videoSizes).map((size) => (
              <button
                key={size.id}
                onClick={() => mediaType === 'image' ? setImageSize(size.id) : setVideoSize(size.id)}
                className={cn(
                  "p-3 rounded-xl text-center transition-all",
                  (mediaType === 'image' ? imageSize : videoSize) === size.id
                    ? "bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/50"
                    : "bg-white/5 border border-white/10 hover:border-white/30"
                )}
                data-testid={`ad-gen-size-${size.id}`}
              >
                <span className="text-xl block mb-1">{size.icon}</span>
                <span className="text-sm font-medium text-white block">{size.label}</span>
                <span className="text-xs text-gray-500">{size.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Video Duration (only for video) */}
        {mediaType === 'video' && (
          <div className="mt-6">
            <label className="text-sm text-gray-400 block mb-3">Video Duration</label>
            <div className="grid grid-cols-3 gap-2">
              {videoDurations.map((dur) => (
                <button
                  key={dur.id}
                  onClick={() => setVideoDuration(dur.id)}
                  className={cn(
                    "p-3 rounded-xl text-center transition-all",
                    videoDuration === dur.id
                      ? "bg-cyan-500/20 border border-cyan-500/50"
                      : "bg-white/5 border border-white/10 hover:border-white/30"
                  )}
                  data-testid={`ad-gen-duration-${dur.id}`}
                >
                  <span className="text-sm font-medium text-white block">{dur.label}</span>
                  <span className="text-xs text-gray-500">{dur.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Logo Upload */}
        <div className="mt-6">
          <label className="text-sm text-gray-400 block mb-3">Upload Logo (Optional)</label>
          <div className="flex items-start gap-4">
            {logoPreview ? (
              <div className="relative">
                <img 
                  src={logoPreview} 
                  alt="Logo preview" 
                  className="w-20 h-20 object-contain bg-white/10 rounded-xl border border-white/20"
                />
                <button
                  onClick={removeLogo}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ) : (
              <label className="w-20 h-20 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-white/5 transition-all">
                <Upload className="w-5 h-5 text-gray-500 mb-1" />
                <span className="text-[10px] text-gray-500">Upload</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="ad-gen-logo-upload"
                />
              </label>
            )}
            <div className="flex-1">
              <p className="text-xs text-gray-500">
                Upload your business logo to include in generated {mediaType === 'video' ? 'videos' : 'images'}. 
                PNG or JPG recommended. <span className="text-green-400 font-medium">Max 20MB.</span>
              </p>
            </div>
          </div>
        </div>

        {/* Custom Prompt Section */}
        <div className="mt-6 p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-purple-500/20">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-white font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                Custom AI Prompt
              </h4>
              <p className="text-xs text-gray-500 mt-1">Use your own specific prompt for image/video generation</p>
            </div>
            <button
              onClick={() => setUseCustomPrompt(!useCustomPrompt)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                useCustomPrompt
                  ? "bg-purple-500 text-white"
                  : "bg-white/10 text-gray-400 hover:bg-white/20"
              )}
              data-testid="ad-gen-custom-prompt-toggle"
            >
              {useCustomPrompt ? 'Custom ON' : 'Use Custom'}
            </button>
          </div>
          
          {useCustomPrompt && (
            <div className="space-y-4">
              {/* Custom Image Prompt */}
              <div>
                <label className="text-xs text-gray-400 block mb-2 flex items-center gap-2">
                  <Image className="w-3 h-3" />
                  Text-to-Image Prompt
                </label>
                <textarea
                  value={customImagePrompt}
                  onChange={(e) => setCustomImagePrompt(e.target.value)}
                  placeholder="Enter a detailed prompt for AI image generation... e.g., 'A professional business woman in a modern office, cinematic lighting, high quality advertising photo, product showcase'"
                  className="w-full p-3 bg-black/30 border border-white/10 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-purple-500/50 focus:outline-none resize-none"
                  rows={3}
                  data-testid="ad-gen-custom-image-prompt"
                />
                <p className="text-[10px] text-gray-600 mt-1">This prompt will be used for Nano Banana AI image generation</p>
              </div>
              
              {/* Custom Video Prompt */}
              <div>
                <label className="text-xs text-gray-400 block mb-2 flex items-center gap-2">
                  <Video className="w-3 h-3" />
                  Text-to-Video Prompt
                </label>
                <textarea
                  value={customVideoPrompt}
                  onChange={(e) => setCustomVideoPrompt(e.target.value)}
                  placeholder="Enter a detailed prompt for AI video generation... e.g., 'A cinematic drone shot over a modern city skyline at sunset, smooth camera movement, professional advertising quality, 4K'"
                  className="w-full p-3 bg-black/30 border border-white/10 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-cyan-500/50 focus:outline-none resize-none"
                  rows={3}
                  data-testid="ad-gen-custom-video-prompt"
                />
                <p className="text-[10px] text-gray-600 mt-1">This prompt will be used for Sora 2 AI video generation</p>
              </div>
              
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-300 flex items-start gap-2">
                  <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Pro Tip:</strong> Be specific! Include details like lighting, camera angle, mood, colors, and style. 
                    The more descriptive your prompt, the better the AI output.
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Video Options (only for video) */}
        {mediaType === 'video' && (
          <div className="mt-6 p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-xl">
            <label className="text-sm text-cyan-400 font-medium block mb-4">Video Options</label>
            <div className="space-y-3">
              {/* Background Music Checkbox */}
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={includeBackgroundMusic}
                    onChange={(e) => setIncludeBackgroundMusic(e.target.checked)}
                    className="sr-only peer"
                    data-testid="ad-gen-music-checkbox"
                  />
                  <div className="w-5 h-5 border-2 border-white/30 rounded peer-checked:bg-cyan-500 peer-checked:border-cyan-500 transition-all flex items-center justify-center">
                    {includeBackgroundMusic && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div>
                  <span className="text-white text-sm group-hover:text-cyan-400 transition-colors">Include Background Music</span>
                  <p className="text-xs text-gray-500">AI will add fitting background music to your video</p>
                </div>
              </label>

              {/* Voice-over Checkbox */}
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={includeVoiceover}
                    onChange={(e) => setIncludeVoiceover(e.target.checked)}
                    className="sr-only peer"
                    data-testid="ad-gen-voiceover-checkbox"
                  />
                  <div className="w-5 h-5 border-2 border-white/30 rounded peer-checked:bg-cyan-500 peer-checked:border-cyan-500 transition-all flex items-center justify-center">
                    {includeVoiceover && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div>
                  <span className="text-white text-sm group-hover:text-cyan-400 transition-colors">Include Voice-over</span>
                  <p className="text-xs text-gray-500">AI will generate voice narration for your ad</p>
                </div>
              </label>
            </div>
          </div>
        )}

        <Button
          onClick={generateAds}
          disabled={loading}
          className="w-full mt-6 bg-gradient-to-r from-purple-500 to-violet-600 text-white py-6"
          data-testid="ad-gen-generate-btn"
        >
          {loading ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating Ads...</>
          ) : (
            <><Sparkles className="w-5 h-5 mr-2" /> Generate 3 Ad Variations</>
          )}
        </Button>
      </div>

      {/* Generated Ads */}
      {generatedAds && generatedAds.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-white">Generated Ads</h3>
              {autoSaving && (
                <span className="flex items-center gap-1.5 px-2 py-1 bg-green-500/10 rounded-full text-xs text-green-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Auto-saving...
                </span>
              )}
              {projectSaved && !autoSaving && (
                <span className="flex items-center gap-1.5 px-2 py-1 bg-green-500/20 rounded-full text-xs text-green-400">
                  <Check className="w-3 h-3" />
                  Auto-saved
                </span>
              )}
            </div>
            <Button
              onClick={saveProject}
              disabled={savingProject}
              variant="outline"
              className={cn(
                "border-green-500/50 text-green-400 hover:bg-green-500/10",
                projectSaved && "border-green-500 bg-green-500/20"
              )}
              data-testid="ad-gen-save-btn"
            >
              {savingProject ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
              ) : projectSaved ? (
                <><Check className="w-4 h-4 mr-2" /> Saved to My Projects</>
              ) : (
                <><FolderOpen className="w-4 h-4 mr-2" /> Save to My Projects</>
              )}
            </Button>
          </div>

          {/* Generated Video (shown once at top if video type selected) */}
          {mediaType === 'video' && (
            <div className="glass rounded-xl p-5 border border-cyan-500/30">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm flex items-center gap-2">
                  <Video className="w-4 h-4" /> Generated Video
                </span>
                <span className="text-xs text-gray-500">Sora 2 AI • {videoDuration}s • {videoSizes.find(s => s.id === videoSize)?.label}</span>
              </div>
              
              {adVideo ? (
                <div className="space-y-3">
                  <div className={cn("rounded-xl overflow-hidden border border-white/10", getMediaAspectClass(), "max-h-[500px]")}>
                    <video 
                      src={adVideo} 
                      controls 
                      autoPlay 
                      muted 
                      loop 
                      className="w-full h-full object-contain bg-black"
                      data-testid="ad-gen-video-result"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={adVideo}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 transition-colors"
                      data-testid="ad-gen-video-download"
                    >
                      <Download className="w-4 h-4" /> Download Video
                    </a>
                    <button
                      onClick={() => generateAdVideo(generatedAds[0]?.headline || formData.product_service)}
                      disabled={generatingMedia.video}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-gray-400 transition-colors"
                    >
                      {generatingMedia.video ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Regenerate
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => generateAdVideo(generatedAds[0]?.headline || formData.product_service)}
                  disabled={generatingMedia.video}
                  className="w-full flex items-center justify-center gap-3 px-4 py-8 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 hover:from-cyan-500/20 hover:to-blue-500/20 border border-cyan-500/30 rounded-xl text-cyan-400 transition-all"
                  data-testid="ad-gen-video-btn"
                >
                  {generatingMedia.video ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Generating Video (this may take 2-5 minutes)...</>
                  ) : (
                    <><Video className="w-5 h-5" /> Generate Ad Video with Sora 2</>
                  )}
                </button>
              )}
            </div>
          )}
          
          {generatedAds.map((ad, index) => (
            <div key={index} className="glass rounded-xl p-5 border border-purple-500/20" data-testid={`ad-gen-result-${index}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                  Variation {index + 1}
                </span>
                <button
                  onClick={() => copyToClipboard(`${ad.headline}\n\n${ad.body}\n\n${ad.cta}`, `ad-${index}`)}
                  className="flex items-center gap-2 px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-gray-400 transition-colors"
                >
                  {copied[`ad-${index}`] ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  Copy All
                </button>
              </div>

              {/* Generated Image (only show for image type) */}
              {mediaType === 'image' && adImages[index] && (
                <div className="mb-4 space-y-2">
                  <div className={cn("rounded-xl overflow-hidden border border-white/10", getMediaAspectClass(), "max-h-[400px]")}>
                    <img 
                      src={adImages[index]} 
                      alt={`Ad ${index + 1}`} 
                      className="w-full h-full object-contain bg-black/50" 
                      data-testid={`ad-gen-image-${index}`}
                    />
                  </div>
                  <a
                    href={adImages[index]}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink-500/20 hover:bg-pink-500/30 border border-pink-500/30 rounded-lg text-pink-400 text-sm transition-colors"
                    data-testid={`ad-gen-image-download-${index}`}
                  >
                    <Download className="w-4 h-4" /> Download Image
                  </a>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <span className="text-xs text-gray-500 block mb-1">HEADLINE</span>
                  <p className="text-white font-bold text-lg">{ad.headline}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block mb-1">BODY COPY</span>
                  <p className="text-gray-300 whitespace-pre-wrap">{ad.body}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block mb-1">CALL TO ACTION</span>
                  <span className="inline-block px-4 py-2 bg-purple-500 text-white rounded-lg font-medium">
                    {ad.cta}
                  </span>
                </div>
                
                {/* Generate Image Button (only for image type) */}
                {mediaType === 'image' && (
                  <button
                    onClick={() => generateAdImage(index, ad.headline)}
                    disabled={generatingMedia[index]}
                    className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-pink-500/20 to-purple-500/20 hover:from-pink-500/30 hover:to-purple-500/30 border border-pink-500/30 rounded-xl text-pink-400 transition-all"
                    data-testid={`ad-gen-img-btn-${index}`}
                  >
                    {generatingMedia[index] ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Generating Image...</>
                    ) : adImages[index] ? (
                      <><RefreshCw className="w-4 h-4" /> Regenerate Image</>
                    ) : (
                      <><Image className="w-4 h-4" /> Generate {imageSizes.find(s => s.id === imageSize)?.label} Image</>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

